import { z } from "zod";

// Phone validation: canonical +639XXXXXXXXX (PH mobile).
export const PhoneSchema = z
  .string()
  .trim()
  .regex(/^\+639\d{9}$/u, "phone must be +639XXXXXXXXX (canonical PH mobile)");

// short_code accepts mixed case (e.g. "BEANBAR" or "beanbar"); the
// server-side lookup (findTenantByShortCode) compares case-insensitively
// and migration 027 guarantees uniqueness on lower(join_short_code), so
// the customer-facing /j/:shortCode and /signup?tenant=... URLs work
// regardless of how the merchant prints the slug on their poster.
export const ShortCodeSchema = z
  .string()
  .trim()
  .min(2)
  .max(64)
  .regex(
    /^[A-Za-z0-9-]+$/u,
    "short_code must be letters, digits, or hyphens",
  );

export const VerticalOsSchema = z.enum([
  "CARWASH",
  "BARESCA",
  "WATERSTATION",
  "CHEFANTHONY",
  "HAOS",
  "LEXOS",
  "AIRCONSERV",
  "PACKDIST",
  "CLINICBEAUTY",
  "STANDALONE",
]);

export const LoyaltyModeSchema = z.enum(["POINTS", "STAMPS", "VISIT_TIERS", "HYBRID"]);
export const SubscriptionTierSchema = z.enum(["FREE", "PRO", "ELITE"]);

// Phase 34 (D107) — re-export the tier feature catalog so PWA + admin
// console import everything from @orbit/api-zod.
export {
  BillingCycleSchema,
  PlanChangeStatusSchema,
  SUBSCRIPTION_TIERS,
  TIER_CATALOG,
  TIER_FEATURE_MATRIX,
  TIER_QUOTAS,
  type BillingCycle,
  type TierQuotas,
  type PlanChangeStatus,
  type SubscriptionTier,
  type TierCatalogEntry,
  type TierFeatureRow,
} from "./tier-catalog.js";

// ----- Envelope helpers -----
export const ApiErrorSchema = z.object({
  code: z.string(),
  message: z.string(),
  details: z.unknown().optional(),
});
export type ApiError = z.infer<typeof ApiErrorSchema>;

export const apiEnvelope = <T extends z.ZodTypeAny>(data: T) =>
  z.object({
    data: data.optional(),
    error: ApiErrorSchema.optional(),
    requestId: z.string(),
  });

// ----- Tokens -----
export const AuthTokensSchema = z.object({
  accessToken: z.string(),
  refreshToken: z.string(),
  accessTokenExpiresAt: z.string(),
  refreshTokenExpiresAt: z.string(),
});
export type AuthTokens = z.infer<typeof AuthTokensSchema>;

// ----- Public: /api/v1/public/tenant-by-short-code/:shortCode -----
export const PublicTenantSchema = z.object({
  name: z.string(),
  verticalOs: VerticalOsSchema,
  loyaltyMode: LoyaltyModeSchema,
  joinShortCode: z.string(),
});
export type PublicTenant = z.infer<typeof PublicTenantSchema>;

// EmailSchema lives here (above SignupViaJoinRequestSchema) because
// Phase 32 (D104) references it from both signup variants AND from
// PasswordResetRequestSchema; module-load order would otherwise hit a
// TDZ on the join schema.
const EmailSchema = z.string().trim().toLowerCase().email("Invalid email.");

// ----- Signup via Join -----
export const SignupViaJoinRequestSchema = z.object({
  tenantShortCode: ShortCodeSchema,
  name: z.string().trim().min(1).max(120),
  phone: PhoneSchema,
  // Phase 32 (D104) — optional at the QR counter for typing-speed
  // reasons, but the post-signup /set-password page nudges the
  // customer to add one before they reach /cards.
  email: EmailSchema.optional(),
  consentAcknowledged: z.literal(true),
});
export type SignupViaJoinRequest = z.infer<typeof SignupViaJoinRequestSchema>;

export const SignupViaJoinResponseSchema = z.object({
  customerId: z.string().uuid(),
  tenantCustomerId: z.string().uuid(),
  tokens: AuthTokensSchema,
});
export type SignupViaJoinResponse = z.infer<typeof SignupViaJoinResponseSchema>;

// ----- Add via Join (authenticated) -----
export const AddViaJoinRequestSchema = z.object({
  tenantShortCode: ShortCodeSchema,
});
export type AddViaJoinRequest = z.infer<typeof AddViaJoinRequestSchema>;

export const AddViaJoinResponseSchema = z.object({
  status: z.enum(["added", "already_enrolled"]),
  tenantCustomerId: z.string().uuid(),
});
export type AddViaJoinResponse = z.infer<typeof AddViaJoinResponseSchema>;

// ===== Phase 7: phone+password auth =====
//
// Customers now sign in with phone + password. The OTP flow is no longer
// the primary credential; it's only used as an *optional, one-shot* phone
// verification that flips a "phone verified" badge on the profile page.
// An optional, parallel email verification flow does the same for email.
//
// The legacy /auth/login/{request,verify}-otp and /auth/signup/{request,
// verify}-otp endpoints are intentionally removed in this phase. Clients
// that hit those paths will see a clean 404 (the unmatched-/api 404
// fallback in routes.ts) — there is no stable contract to keep alive
// because there are no customers in production yet.

// 12-char minimum lines up with the admin password rules
// (see routes-admin.PasswordSchema). Maxing at 200 keeps a malicious
// client from forcing the bcrypt cost to chew on a megabyte of input.
const CustomerPasswordSchema = z
  .string()
  .min(12, "Password must be at least 12 characters.")
  .max(200);
// EmailSchema moved up to module-top (above SignupViaJoinRequestSchema).

// ----- Signup: phone + password (one-shot, no OTP) -----
export const SignupRequestSchema = z.object({
  name: z.string().trim().min(1).max(120),
  phone: PhoneSchema,
  password: CustomerPasswordSchema,
  // Phase 32 (D104) — email is REQUIRED on public signup so every new
  // account ships with two independent recovery channels (phone OTP +
  // email OTP) from day one. It's still NOT verified at signup; the
  // customer unlocks the verified badge later via
  // /auth/verify-email/request. Existing pre-Phase-32 customers without
  // an email continue to function — only NEW signups via this endpoint
  // are gated.
  email: EmailSchema,
  consentAcknowledged: z.literal(true),
});
export type SignupRequest = z.infer<typeof SignupRequestSchema>;

export const SignupResponseSchema = z.object({
  customerId: z.string().uuid(),
  tokens: AuthTokensSchema,
});
export type SignupResponse = z.infer<typeof SignupResponseSchema>;

// ----- Login: phone + password -----
export const LoginRequestSchema = z.object({
  phone: PhoneSchema,
  // Plain string (not the strong CustomerPasswordSchema) so we don't leak
  // the policy via 422 errors on login attempts with shorter legacy
  // passwords that don't apply to Orbit but might exist in muscle memory.
  // Auth failures still surface as a uniform 401.
  password: z.string().min(1).max(200),
});
export type LoginRequest = z.infer<typeof LoginRequestSchema>;

export const LoginResponseSchema = z.object({
  customerId: z.string().uuid(),
  tokens: AuthTokensSchema,
});
export type LoginResponse = z.infer<typeof LoginResponseSchema>;

// ----- Verify phone (authenticated, one-shot) -----
export const VerifyPhoneRequestSchema = z.object({});
export type VerifyPhoneRequest = z.infer<typeof VerifyPhoneRequestSchema>;

// The route returns one of two shapes: a freshly-issued challenge,
// or an idempotent `{ alreadyVerified: true }` when the phone is
// already verified (so re-clicking "Verify now" is a no-op rather
// than a 409). A union keeps both shapes typesafe at the client.
export const VerifyPhoneRequestResponseSchema = z.union([
  z.object({
    otpRequestId: z.string().uuid(),
    expiresAt: z.string(),
  }),
  z.object({
    alreadyVerified: z.literal(true),
  }),
]);
export type VerifyPhoneRequestResponse = z.infer<typeof VerifyPhoneRequestResponseSchema>;

export const VerifyPhoneConfirmRequestSchema = z.object({
  otpRequestId: z.string().uuid(),
  code: z.string().regex(/^\d{6}$/u, "code must be exactly 6 digits"),
});
export type VerifyPhoneConfirmRequest = z.infer<typeof VerifyPhoneConfirmRequestSchema>;

export const VerifyPhoneConfirmResponseSchema = z.object({
  phoneVerified: z.literal(true),
});
export type VerifyPhoneConfirmResponse = z.infer<typeof VerifyPhoneConfirmResponseSchema>;

// ----- Verify email (authenticated, one-shot) -----
//
// `request` is no-body — the email address is taken from the customer
// row so a token-bearing client can't request a code for a different
// address. To change emails, hit the (future) PATCH /customer/me first.
export const VerifyEmailRequestSchema = z.object({});
export type VerifyEmailRequest = z.infer<typeof VerifyEmailRequestSchema>;

// Same dual-shape union as VerifyPhoneRequestResponseSchema — the
// route emits `{ alreadyVerified: true }` when the email is already
// verified so the client can swallow the no-op gracefully.
export const VerifyEmailRequestResponseSchema = z.union([
  z.object({
    otpRequestId: z.string().uuid(),
    expiresAt: z.string(),
  }),
  z.object({
    alreadyVerified: z.literal(true),
  }),
]);
export type VerifyEmailRequestResponse = z.infer<typeof VerifyEmailRequestResponseSchema>;

export const VerifyEmailConfirmRequestSchema = z.object({
  otpRequestId: z.string().uuid(),
  code: z.string().regex(/^\d{6}$/u, "code must be exactly 6 digits"),
});
export type VerifyEmailConfirmRequest = z.infer<typeof VerifyEmailConfirmRequestSchema>;

export const VerifyEmailConfirmResponseSchema = z.object({
  emailVerified: z.literal(true),
});
export type VerifyEmailConfirmResponse = z.infer<typeof VerifyEmailConfirmResponseSchema>;

// ----- Public signup (phone + name, no merchant context) -----
//
// Two-step OTP flow for customers who arrive at Orbit without scanning a
// merchant's Join QR (e.g. via a social post or a shared link). Mirrors
// the LOGIN OTP shape so the rate-limit / lockout / SMS_NOT_CONFIGURED
// machinery is shared end-to-end:
//
//   1. POST /auth/signup/request-otp { phone, name } -> { otpRequestId }
//      • 409 PHONE_ALREADY_REGISTERED when the phone is already on file
//        (matches the existing /enrollment/signup-via-join semantics —
//        public signup intentionally does NOT hide existence here, since
//        the user is asserting they are new).
//      • 503 SMS_NOT_CONFIGURED when the superadmin hasn't picked an
//        SMS provider yet, identical to the login path.
//   2. POST /auth/signup/verify-otp { otpRequestId, code, name }
//      -> { customerId, tokens }
//      • Creates the global customer record (no tenant_customers row),
//        writes the consent log, and issues the same session tokens as
//        the QR signup flow. The new customer lands on /cards with the
//        empty-state prompting them to find a participating merchant.
//      • `name` is collected on step 1 for UX continuity but re-asserted
//        here so the server never has to stash a half-formed identity
//        between the two requests.
export const PublicSignupRequestOtpRequestSchema = z.object({
  phone: PhoneSchema,
  name: z.string().trim().min(1).max(120),
  consentAcknowledged: z.literal(true),
});
export type PublicSignupRequestOtpRequest = z.infer<
  typeof PublicSignupRequestOtpRequestSchema
>;

export const PublicSignupRequestOtpResponseSchema = z.object({
  otpRequestId: z.string().uuid(),
  expiresAt: z.string(),
});
export type PublicSignupRequestOtpResponse = z.infer<
  typeof PublicSignupRequestOtpResponseSchema
>;

export const PublicSignupVerifyOtpRequestSchema = z.object({
  otpRequestId: z.string().uuid(),
  code: z.string().regex(/^\d{6}$/u, "code must be exactly 6 digits"),
  name: z.string().trim().min(1).max(120),
});
export type PublicSignupVerifyOtpRequest = z.infer<
  typeof PublicSignupVerifyOtpRequestSchema
>;

export const PublicSignupVerifyOtpResponseSchema = z.object({
  customerId: z.string().uuid(),
  tokens: AuthTokensSchema,
});
export type PublicSignupVerifyOtpResponse = z.infer<
  typeof PublicSignupVerifyOtpResponseSchema
>;

// ----- Refresh -----
export const RefreshAuthRequestSchema = z.object({
  refreshToken: z.string().min(1),
});
export type RefreshAuthRequest = z.infer<typeof RefreshAuthRequestSchema>;

export const RefreshAuthResponseSchema = z.object({
  tokens: AuthTokensSchema,
});
export type RefreshAuthResponse = z.infer<typeof RefreshAuthResponseSchema>;

// ----- Tenant me (tenant-auth) -----
//
// `maxTransactionAmount` is the per-tenant ceiling for a single
// `POST /tenant/scan` (see `tenants.max_transaction_amount`, migration
// 007). It is exposed on the "me" response so the cashier UI can render
// "₱100,000 max" alongside the AMOUNT_OVER_LIMIT error and the merchant
// settings screen can pre-fill its current value. Serialised as the
// canonical 2-decimal numeric string Postgres `numeric(12, 2)` returns,
// or `null` when the merchant has explicitly disabled the rail.
export const TenantMaxTransactionAmountSchema = z
  .string()
  .regex(/^\d+\.\d{2}$/u, "must be a 2-decimal numeric string");

// Phase 8 — scan_mode + points_rule wire shapes -------------------
//
// `scanMode` is the discriminator that flips POST /tenant/scan
// between "amount required + per-peso accrual" (PER_TRANSACTION,
// the default) and "amount optional + per-visit accrual" (VISIT_ONLY).
//
// `pointsRule` is an explicit accrual rule for POINTS / HYBRID
// loyalty modes. The two valid shapes are:
//   { mode: "PER_PESO",  rate:   <number 0.01..100>  }
//   { mode: "PER_VISIT", amount: <integer 1..1000> }
// The (loyaltyMode, scanMode, pointsRule) matrix is enforced
// server-side in tenant-settings.ts; the wire schema only validates
// the per-shape numeric bounds.
export const ScanModeSchema = z.enum(["PER_TRANSACTION", "VISIT_ONLY"]);
export type ScanMode = z.infer<typeof ScanModeSchema>;

export const PointsRulePerPesoSchema = z.object({
  mode: z.literal("PER_PESO"),
  rate: z.number().positive().min(0.01).max(100),
});
export const PointsRulePerVisitSchema = z.object({
  mode: z.literal("PER_VISIT"),
  amount: z.number().int().min(1).max(1000),
});
// D99 — AMOUNT_PER_POINT: spend ₱amount to earn 1 point.
// `amount` is integer pesos (no centavos). 1..10000 range so a
// fat-finger entry of 1,000,000 doesn't accidentally make every
// scan worth zero points. Valid only when scan_mode is
// PER_TRANSACTION (matrix enforced in tenant-settings.ts).
export const PointsRuleAmountPerPointSchema = z.object({
  mode: z.literal("AMOUNT_PER_POINT"),
  amount: z.number().int().min(1).max(10000),
});
export const PointsRuleSchema = z.discriminatedUnion("mode", [
  PointsRulePerPesoSchema,
  PointsRulePerVisitSchema,
  PointsRuleAmountPerPointSchema,
]);
export type PointsRule = z.infer<typeof PointsRuleSchema>;

export const TenantMeResponseSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  verticalOs: VerticalOsSchema,
  subscriptionTier: SubscriptionTierSchema,
  loyaltyMode: LoyaltyModeSchema,
  maxTransactionAmount: TenantMaxTransactionAmountSchema.nullable(),
  // Phase 8 additions — present on all responses; default values are
  // applied server-side at the migration boundary so legacy clients
  // that don't read these fields still work unchanged.
  scanMode: ScanModeSchema,
  pointsRule: PointsRuleSchema,
});
export type TenantMeResponse = z.infer<typeof TenantMeResponseSchema>;

// ----- PATCH /api/v1/tenant/settings -----
//
// Merchant-side write surface for the per-tenant scan settings exposed
// on `/tenant/me`. Today the only knob is `maxTransactionAmount`; we
// keep the request shape an object so future fields (auto-disable on
// chargeback, etc.) can be added without breaking the contract.
//
// Field semantics:
//   • `null` — explicitly disable the per-transaction ceiling. The
//     scan endpoint will accept any in-range amount (subject to the
//     hard `numeric(12, 2)` column bound enforced by Zod on the request).
//   • a 2-decimal string — set a specific limit. We accept the value
//     as a string (not a number) so callers can't lose cents to
//     IEEE-754 rounding on the wire ("100000.01" ≠ 100000.009999...).
//     The string must be > 0 and ≤ 9_999_999_999.99 to fit the
//     numeric(12, 2) column without overflow.
//
// Omitted fields = no change. We deliberately distinguish "field not
// present" (leave alone) from "field present and null" (clear it) so a
// merchant who only wants to disable the ceiling doesn't have to
// re-send unrelated settings.
const MAX_LIMIT_INTEGER = 9_999_999_999n;
const MAX_LIMIT_FRACTION = 99n;

export const TenantMaxTransactionAmountInputSchema =
  TenantMaxTransactionAmountSchema.refine(
    (value) => {
      const [intPart, fracPart] = value.split(".") as [string, string];
      // Strip a single leading zero on integer part — but only when the
      // integer is exactly "0" (so "0.50" is valid, "01.00" is not).
      if (intPart.length > 1 && intPart.startsWith("0")) return false;
      // Reject ".00" total — the ceiling must be strictly positive so
      // a typo can't lock the tenant out of every scan. Use null to
      // disable.
      if (intPart === "0" && fracPart === "00") return false;
      // Range check against the column's numeric(12, 2) bound. Use
      // BigInt so callers can't sneak past with a value JS Number
      // would round (e.g. 9999999999.999).
      const intBig = BigInt(intPart);
      if (intBig > MAX_LIMIT_INTEGER) return false;
      if (intBig === MAX_LIMIT_INTEGER && BigInt(fracPart) > MAX_LIMIT_FRACTION)
        return false;
      return true;
    },
    {
      message:
        "must be > 0.00 and ≤ 9999999999.99 (the numeric(12, 2) column bound)",
    },
  );

export const TenantSettingsUpdateRequestSchema = z
  .object({
    maxTransactionAmount: TenantMaxTransactionAmountInputSchema.nullable().optional(),
    // Phase 8 — optional knobs; same "field present overwrites,
    // omitted leaves alone" semantics as maxTransactionAmount above.
    scanMode: ScanModeSchema.optional(),
    pointsRule: PointsRuleSchema.optional(),
  })
  // Reject empty bodies so a misconfigured client doesn't silently
  // get a no-op 200. The handler also returns the freshest "me" row
  // so callers don't have to re-fetch.
  .refine(
    (v) =>
      v.maxTransactionAmount !== undefined ||
      v.scanMode !== undefined ||
      v.pointsRule !== undefined,
    { message: "request must include at least one settings field" },
  );
export type TenantSettingsUpdateRequest = z.infer<typeof TenantSettingsUpdateRequestSchema>;

// ----- Customer me (customer-auth) -----
//
// Phase 7 surfaces the optional email + the two verified-badge bits so
// the profile page can render "Phone verified" / "Email verified" CTAs
// without a second round-trip. Email is masked the same way phone is —
// the client should never see the raw address it doesn't already own.
export const CustomerMeResponseSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  phone: z.string(), // returned masked: "+63 *** *** 4567"
  birthday: z.string().nullable(),
  phoneVerified: z.boolean(),
  email: z.string().nullable(), // returned masked: "m***a@example.com"
  emailVerified: z.boolean(),
  // Phase 31 (D103) — true when the customer has any non-null
  // password_hash on file. The PWA uses this to (a) drive the
  // "Set up a password" prompt for users who joined via QR before
  // we made password setup a required post-join step, and
  // (b) decide whether the SetPassword page must require a
  // currentPassword input (true) or accept just newPassword (false).
  // Additive — pre-Phase-31 clients ignore unknown fields.
  passwordSet: z.boolean(),
  // Phase 32 (D104) — true when the customer has any non-null email
  // on file (independent of emailVerified). The PWA uses this to
  // (a) decide whether the Forgot-password channel selector should
  // offer the Email option, and (b) decide whether SetPassword
  // should surface its optional email-capture field. Additive —
  // pre-Phase-32 clients ignore unknown fields.
  emailSet: z.boolean(),
  // Phase 18 — first meaningful customer event timestamp (D65). The
  // PWA uses this to decide WHEN to show the push-permission prompt:
  // null means "no scan / claim / voucher yet — too early to ask".
  // Additive ISO8601 string; the legacy customer-me consumers ignore
  // unknown fields so this is backwards compatible.
  firstMeaningfulEventAt: z.string().nullable(),
});
export type CustomerMeResponse = z.infer<typeof CustomerMeResponseSchema>;

// ===== Phase 2: Identity QR + Cards =====

export const TenantCustomerStatusSchema = z.enum([
  "ACTIVE",
  "CHURNED",
  "BANNED",
  "PAUSED",
  // Phase 12 — customer-initiated revoke. Mirrored from the
  // tenant_customer_status enum (migration 013).
  "REVOKED",
]);
export type TenantCustomerStatus = z.infer<typeof TenantCustomerStatusSchema>;

// ----- GET /api/v1/identity/generate -----
// `ttlSec` lets the client schedule its next refresh independently of
// `expiresAt`. Relying purely on `expiresAt - Date.now()` was fragile:
// any client clock skew (≥ ~54s ahead, observed in the wild on phones
// with mis-set time) collapsed the refresh interval to the 1-second
// floor and the modal hammered /identity/generate every second.
export const IdentityGenerateResponseSchema = z.object({
  qrToken: z.string(),
  expiresAt: z.string(),
  ttlSec: z.number().int().positive(),
});
export type IdentityGenerateResponse = z.infer<typeof IdentityGenerateResponseSchema>;

// ----- GET /api/v1/customer/me/cards -----
export const TenantCardSchema = z.object({
  tenantCustomerId: z.string().uuid(),
  tenantId: z.string().uuid(),
  tenantName: z.string(),
  verticalOs: VerticalOsSchema,
  loyaltyMode: LoyaltyModeSchema,
  joinedAt: z.string(),
  totalVisits: z.number().int().nonnegative(),
  currentStreak: z.number().int().nonnegative(),
  longestStreak: z.number().int().nonnegative(),
  // Phase 5 loyalty balances. Always present; the meaningful value
  // depends on `loyaltyMode`:
  //   • POINTS       → pointsBalance is live, stampsCount stays 0
  //   • STAMPS       → stampsCount is live, pointsBalance stays 0
  //   • VISIT_TIERS  → both stay 0; the PWA renders totalVisits instead
  //   • HYBRID       → both are live
  pointsBalance: z.number().multipleOf(0.01).nonnegative(),
  stampsCount: z.number().int().nonnegative(),
  // Per-merchant reward target for STAMPS / HYBRID cards. Always present
  // (defaults to 10 server-side) so the PWA can render "{stampsCount} /
  // {stampsRewardThreshold} stamps" without a fallback literal.
  stampsRewardThreshold: z.number().int().positive(),
  status: TenantCustomerStatusSchema,
});
export type TenantCard = z.infer<typeof TenantCardSchema>;

export const CustomerCardsResponseSchema = z.object({
  cards: z.array(TenantCardSchema),
});
export type CustomerCardsResponse = z.infer<typeof CustomerCardsResponseSchema>;

// ===== Phase 4: Cashier scan-customer-QR =====

// ----- POST /api/v1/tenant/scan -----
//
// Cashier app POSTs the customer's just-scanned Identity QR token plus
// the transaction amount. Server verifies the QR (signature + exp +
// nonce-not-yet-consumed) then writes a tenant-scoped `transactions`
// row. Amount is constrained to two decimal places and a generous upper
// bound — the numeric(12, 2) column accepts 10 integer digits, which
// covers anything a Philippine SME will see in a single transaction
// (₱9,999,999,999.99).
export const TenantScanRequestSchema = z.object({
  qrToken: z.string().min(1),
  amount: z
    .number()
    .nonnegative()
    .multipleOf(0.01)
    .max(9_999_999_999.99),
});
export type TenantScanRequest = z.infer<typeof TenantScanRequestSchema>;

// Phase 8 — flexible scan request. `amount` is OPTIONAL and may be
// explicit `null` on the wire here; the Phase 8 handler then applies
// the mode-aware rule:
//   • PER_TRANSACTION tenant + amount missing/null → 400 AMOUNT_REQUIRED
//   • VISIT_ONLY      tenant + amount missing/null → record visit, amount=null
//   • either tenant   + amount present (number)    → use it (visit-only
//     records it as metadata; per-transaction uses it for accrual).
// Accepting `null` explicitly (in addition to omitting the field)
// matches the spec wording "amount: number | null" and lets thin
// clients post a stable body shape regardless of mode without
// branching the JSON.
// The numeric bounds (positive, 2dp, ceiling) match the Phase 5
// schema above so behaviour for PER_TRANSACTION tenants is unchanged.
export const TenantScanRequestSchemaV2 = z
  .object({
    qrToken: z.string().min(1),
    amount: z
      .number()
      .positive()
      .multipleOf(0.01)
      .max(9_999_999_999.99)
      .nullable()
      .optional(),
    // D118 — accept `amountCentavos` (positive integer centavos) as
    // an alias for `amount`. External POS clients (notably Baresca OS)
    // were posting `{qrToken, amountCentavos}` against a schema that
    // only knew `amount`; Zod silently dropped the unknown field, the
    // handler then returned 400 AMOUNT_REQUIRED, and the embedded POS
    // webview rendered the rejected response as a blank page. Keeping
    // `amount` (pesos) as the canonical wire field for back-compat,
    // we now also accept centavos and convert. If both are supplied
    // they must agree (otherwise the request is rejected by the
    // refinement below) so a misconfigured client can't silently
    // accrue against the wrong number.
    amountCentavos: z
      .number()
      .int()
      .positive()
      .max(999_999_999_999)
      .nullable()
      .optional(),
  })
  .superRefine((val, ctx) => {
    if (
      val.amount !== undefined &&
      val.amount !== null &&
      val.amountCentavos !== undefined &&
      val.amountCentavos !== null
    ) {
      const fromCents = Math.round(val.amountCentavos) / 100;
      // Compare with a tiny epsilon — both sides are 2dp by construction.
      if (Math.abs(fromCents - val.amount) > 0.005) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["amountCentavos"],
          message: "amount and amountCentavos disagree.",
        });
      }
    }
  })
  .transform((val) => {
    // Normalise: if only amountCentavos was supplied, hoist it into
    // `amount` so every downstream check (mode-required, ceiling,
    // accrual) reads a single field.
    if (
      (val.amount === undefined || val.amount === null) &&
      val.amountCentavos !== undefined &&
      val.amountCentavos !== null
    ) {
      return { ...val, amount: Math.round(val.amountCentavos) / 100 };
    }
    return val;
  });
export type TenantScanRequestV2 = z.infer<typeof TenantScanRequestSchemaV2>;

export const TenantScanResponseSchema = z.object({
  transactionId: z.string().uuid(),
  customerName: z.string(),
  // Phase 5 post-scan loyalty balances. The cashier app renders
  // "Maria now has 179 points" / "Maria now has 6 stamps" straight off
  // the scan response, so we hand them back the absolute values that
  // landed on `tenant_customers` after the accrual UPDATE — not the
  // delta. Always present; the meaningful value depends on the
  // tenant's loyaltyMode (see TenantCardSchema for the per-mode
  // semantics — both columns are 0 in modes that don't use them).
  pointsBalance: z.number().multipleOf(0.01).nonnegative(),
  stampsCount: z.number().int().nonnegative(),
});
export type TenantScanResponse = z.infer<typeof TenantScanResponseSchema>;

// ----- GET /api/v1/tenant/transactions -----
//
// Merchant-facing read of the tenant-scoped transactions ledger written
// by POST /tenant/scan. Designed for "what visits happened today / this
// week" reconciliation views — most-recent-first and cursor-paginated so
// scrolling deep into history stays cheap (the
// `transactions_tenant_created_at_idx` index walks (tenant_id, created_at)
// in reverse without an in-memory sort).
//
// Cursor format is opaque to clients: the server emits `nextCursor` from
// the last item of the page and round-trips it back unchanged. We
// deliberately don't expose limit/offset semantics — a tenant racing
// inserts against pagination shouldn't see duplicated or skipped rows.
//
// `from` / `to` are ISO-8601 datetimes; the window is [from, to)
// (inclusive lower bound, exclusive upper bound) so callers can chain
// "yesterday" + "today" without overlap.
export const TenantTransactionsQuerySchema = z.object({
  from: z.string().datetime({ offset: true }).optional(),
  to: z.string().datetime({ offset: true }).optional(),
  customerId: z.string().uuid().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(20),
  cursor: z.string().min(1).optional(),
});
export type TenantTransactionsQuery = z.infer<typeof TenantTransactionsQuerySchema>;

export const TenantTransactionItemSchema = z.object({
  id: z.string().uuid(),
  customerId: z.string().uuid(),
  customerName: z.string(),
  amount: z.string(),
  createdAt: z.string(),
});
export type TenantTransactionItem = z.infer<typeof TenantTransactionItemSchema>;

export const TenantTransactionsResponseSchema = z.object({
  items: z.array(TenantTransactionItemSchema),
  nextCursor: z.string().nullable(),
});
export type TenantTransactionsResponse = z.infer<typeof TenantTransactionsResponseSchema>;

// ----- GET /api/v1/tenant/customers/:id -----
//
// Merchant-side customer lookup. Returns the live loyalty balances and
// activity counters for one of *this tenant's* enrolled customers, so
// the admin dashboard / cashier app can answer "how many points does
// Maria have right now?" without going through the customer's own
// /customer/me/cards endpoint.
//
// `id` in the path is the customers.id (the same UUID the cashier sees
// on /tenant/transactions rows), not the join-row id, so the merchant
// can drive this endpoint straight off a transaction-list row click.
//
// RLS isolates the read: a merchant can only resolve customer ids that
// have a tenant_customers row under their tenant. Foreign customer ids
// surface as 404, matching the customer-not-enrolled case — we don't
// distinguish "doesn't exist" from "exists but isn't yours" so the API
// can't be used as an enrolment oracle by a malicious tenant token.
export const TenantCustomerLookupResponseSchema = z.object({
  customerId: z.string().uuid(),
  name: z.string(),
  joinedAt: z.string(),
  lastVisitAt: z.string().nullable(),
  totalVisits: z.number().int().nonnegative(),
  totalSpend: z.string(),
  // Same per-mode semantics as TenantCardSchema: the columns that don't
  // apply to the tenant's loyaltyMode stay at 0.
  pointsBalance: z.number().multipleOf(0.01).nonnegative(),
  stampsCount: z.number().int().nonnegative(),
  status: TenantCustomerStatusSchema,
});
export type TenantCustomerLookupResponse = z.infer<
  typeof TenantCustomerLookupResponseSchema
>;

// Typed error code returned by POST /api/v1/tenant/scan when the
// cashier-entered amount exceeds the tenant's configured per-transaction
// ceiling (`tenants.max_transaction_amount`). The error envelope's
// `details` carries the current limit as a canonical 2-decimal string
// so the cashier UI can surface "₱100,000 max — please confirm".
export const SCAN_ERROR_AMOUNT_OVER_LIMIT = "AMOUNT_OVER_LIMIT" as const;
export type ScanAmountOverLimitCode = typeof SCAN_ERROR_AMOUNT_OVER_LIMIT;

export const ScanAmountOverLimitDetailsSchema = z.object({
  limit: z.string().regex(/^\d+\.\d{2}$/u, "limit must be a 2-decimal numeric string"),
});
export type ScanAmountOverLimitDetails = z.infer<typeof ScanAmountOverLimitDetailsSchema>;

// ----- Phone normalisation utility -----
const PH_PHONE_REGEX = /^\+?(63)?0?9(\d{9})$/u;

export function canonicalizePhone(input: string): string | null {
  const cleaned = input.replace(/[\s\-()]/gu, "");
  const match = cleaned.match(PH_PHONE_REGEX);
  if (!match) return null;
  return `+639${match[2]}`;
}

export function maskPhone(canonical: string): string {
  if (canonical.length < 6) return "***";
  return `${canonical.slice(0, 4)} *** *** ${canonical.slice(-4)}`;
}

export function maskPhoneShort(canonical: string): string {
  if (canonical.length < 4) return "***";
  return `****${canonical.slice(-4)}`;
}

// =====================================================================
// Phase 9 — Reward Catalog & Redemption
// =====================================================================
//
// All shapes the merchant + customer routes use for the reward catalog
// (CRUD), redemption requests (customer side), and voucher lifecycle
// (cashier side). The DB-level invariants (CHECK constraints, RLS,
// unique voucher_code) are mirrored at this API boundary so the
// caller gets a 400 with a readable message before any tx work.

export const RewardTypeSchema = z.enum([
  "DISCOUNT_PESO",
  "DISCOUNT_PERCENT",
  "FREE_ITEM",
  "OTHER",
]);
export type RewardType = z.infer<typeof RewardTypeSchema>;

// Discriminated metadata by reward `type`. Stored as `jsonb` on the
// row so we can grow a new reward type without a migration; the
// runtime contract is anchored here.
export const RewardTypeMetadataSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("DISCOUNT_PESO"),
    amount: z.number().positive().max(100_000),
  }),
  z.object({
    type: z.literal("DISCOUNT_PERCENT"),
    percent: z.number().int().min(1).max(100),
  }),
  z.object({
    type: z.literal("FREE_ITEM"),
    itemName: z.string().trim().min(1).max(120),
  }),
  z.object({ type: z.literal("OTHER") }),
]);
export type RewardTypeMetadata = z.infer<typeof RewardTypeMetadataSchema>;

// Common reward fields shared between create and update.
const RewardCommonFields = {
  name: z.string().trim().min(1).max(120),
  description: z.string().trim().max(2_000).nullable().optional(),
  imageUrl: z.string().url().max(2_048).nullable().optional(),
  pointsCost: z.number().multipleOf(0.01).positive().max(1_000_000).nullable().optional(),
  stampsCost: z.number().int().positive().max(10_000).nullable().optional(),
  voucherTtlDays: z.number().int().positive().max(3_650).optional(),
  stockLimit: z.number().int().positive().max(1_000_000).nullable().optional(),
} as const;

export const CreateRewardRequestSchema = z
  .object({
    ...RewardCommonFields,
    metadata: RewardTypeMetadataSchema,
  })
  .refine(
    (v) => v.pointsCost != null || v.stampsCost != null,
    {
      message: "At least one of pointsCost or stampsCost is required.",
      path: ["pointsCost"],
    },
  );
export type CreateRewardRequest = z.infer<typeof CreateRewardRequestSchema>;

export const UpdateRewardRequestSchema = z
  .object({
    ...RewardCommonFields,
    metadata: RewardTypeMetadataSchema.optional(),
    active: z.boolean().optional(),
  })
  .partial();
export type UpdateRewardRequest = z.infer<typeof UpdateRewardRequestSchema>;

export const RewardResponseSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  name: z.string(),
  description: z.string().nullable(),
  imageUrl: z.string().nullable(),
  type: RewardTypeSchema,
  metadata: RewardTypeMetadataSchema,
  pointsCost: z.number().multipleOf(0.01).nullable(),
  stampsCost: z.number().int().nullable(),
  voucherTtlDays: z.number().int().positive(),
  stockLimit: z.number().int().nullable(),
  stockRemaining: z.number().int().nullable(),
  active: z.boolean(),
  archivedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type RewardResponse = z.infer<typeof RewardResponseSchema>;

export const RedemptionStatusSchema = z.enum([
  "ISSUED",
  "REDEEMED",
  "EXPIRED",
  "CANCELLED",
]);
export type RedemptionStatus = z.infer<typeof RedemptionStatusSchema>;

// Cashier / merchant-facing redemption row — includes everything
// (customer name, reward snapshot, audit fields).
export const RedemptionResponseSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  tenantCustomerId: z.string().uuid(),
  customerId: z.string().uuid(),
  customerName: z.string(),
  rewardId: z.string().uuid(),
  rewardName: z.string(),
  rewardType: RewardTypeSchema,
  rewardMetadata: RewardTypeMetadataSchema,
  voucherCode: z.string(),
  status: RedemptionStatusSchema,
  pointsSpent: z.number().multipleOf(0.01).nullable(),
  stampsSpent: z.number().int().nullable(),
  issuedAt: z.string(),
  expiresAt: z.string(),
  redeemedAt: z.string().nullable(),
  redeemedByActor: z.string().nullable(),
  cancelledAt: z.string().nullable(),
  cancelReason: z.string().nullable(),
});
export type RedemptionResponse = z.infer<typeof RedemptionResponseSchema>;

// Customer-facing voucher row. Same shape as RedemptionResponse but
// with the merchant's name + brand colours so the PWA can render the
// voucher without a second round-trip, and without the cashier-only
// audit fields (redeemedByActor, cancelReason) — those are surfaced
// only via the merchant flow.
export const VoucherResponseSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  tenantName: z.string(),
  tenantBrandColorPrimary: z.string().nullable(),
  rewardId: z.string().uuid(),
  rewardName: z.string(),
  rewardDescription: z.string().nullable(),
  rewardType: RewardTypeSchema,
  rewardMetadata: RewardTypeMetadataSchema,
  voucherCode: z.string(),
  status: RedemptionStatusSchema,
  pointsSpent: z.number().multipleOf(0.01).nullable(),
  stampsSpent: z.number().int().nullable(),
  issuedAt: z.string(),
  expiresAt: z.string(),
  redeemedAt: z.string().nullable(),
});
export type VoucherResponse = z.infer<typeof VoucherResponseSchema>;

// ----- Customer-side request to issue a voucher -----
export const RequestRedemptionRequestSchema = z.object({
  rewardId: z.string().uuid(),
  paymentMethod: z.enum(["points", "stamps"]),
});
export type RequestRedemptionRequest = z.infer<
  typeof RequestRedemptionRequestSchema
>;

// ----- Merchant-side voucher actions -----
export const RedeemVoucherRequestSchema = z.object({
  voucherCode: z.string().trim().min(3).max(64),
});
export type RedeemVoucherRequest = z.infer<typeof RedeemVoucherRequestSchema>;

export const CancelRedemptionRequestSchema = z.object({
  reason: z.string().trim().min(5).max(500),
});
export type CancelRedemptionRequest = z.infer<
  typeof CancelRedemptionRequestSchema
>;

// ----- Available rewards (customer-facing list with eligibility) -----
//
// For each catalog reward we pre-compute whether the customer can
// redeem it right now and, if not, why. The PWA renders the disabled
// state straight off `canRedeem === false` and shows `unmetReason` as
// the helper text — so the same shape drives the merchant preview and
// the customer's "Available rewards" tile.
export const AvailableRewardSchema = RewardResponseSchema.extend({
  canRedeem: z.boolean(),
  // When `canRedeem === false`, one of: INSUFFICIENT_POINTS,
  // INSUFFICIENT_STAMPS, OUT_OF_STOCK, INACTIVE, INELIGIBLE_MODE.
  // Null when canRedeem === true.
  unmetReason: z
    .enum([
      "INSUFFICIENT_POINTS",
      "INSUFFICIENT_STAMPS",
      "OUT_OF_STOCK",
      "INACTIVE",
      "INELIGIBLE_MODE",
    ])
    .nullable(),
});
export type AvailableReward = z.infer<typeof AvailableRewardSchema>;

// ----- Activity feed (transactions ∪ redemptions) -----
//
// Customer card detail page shows a unified timeline. Discriminated by
// `kind` so the renderer can branch without re-fetching either source
// list separately.
export const ActivityFeedItemSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("transaction"),
    id: z.string().uuid(),
    occurredAt: z.string(),
    amount: z.string().nullable(),
    pointsDelta: z.number().multipleOf(0.01),
    stampsDelta: z.number().int(),
    // Phase 10 — surfaced so the customer PWA can render neutral
    // "Voided" / "Refunded ₱X" labels next to the row. Reasons are
    // intentionally NOT exposed customer-side (merchant-only audit).
    // Enum is inlined (instead of referencing TransactionStatusSchema)
    // because the canonical schema is declared later in this file
    // and using it here would trip module-init temporal-dead-zone.
    status: z.enum(["COMPLETED", "VOIDED", "PARTIAL_REFUND", "REFUNDED"]),
    refundedAmount: z.string(),
  }),
  z.object({
    kind: z.literal("redemption"),
    id: z.string().uuid(),
    occurredAt: z.string(),
    voucherCode: z.string(),
    rewardName: z.string(),
    status: RedemptionStatusSchema,
    pointsSpent: z.number().multipleOf(0.01).nullable(),
    stampsSpent: z.number().int().nullable(),
  }),
]);
export type ActivityFeedItem = z.infer<typeof ActivityFeedItemSchema>;

// ----- Phase 9 error codes (echoed in the envelope's error.code) ---
// Surfaced verbatim by the route handlers so the PWA / cashier UI can
// match on a stable string instead of substring-sniffing the message.
export const REDEMPTION_INSUFFICIENT_BALANCE = "INSUFFICIENT_BALANCE" as const;
export const REDEMPTION_OUT_OF_STOCK = "OUT_OF_STOCK" as const;
export const REDEMPTION_REWARD_INACTIVE = "REWARD_INACTIVE" as const;
export const REDEMPTION_REWARD_INELIGIBLE_FOR_MODE =
  "REWARD_INELIGIBLE_FOR_MODE" as const;
export const REDEMPTION_INVALID_PAYMENT_METHOD =
  "INVALID_PAYMENT_METHOD" as const;
export const REDEMPTION_VOUCHER_NOT_FOUND = "VOUCHER_NOT_FOUND" as const;
export const REDEMPTION_VOUCHER_EXPIRED = "VOUCHER_EXPIRED" as const;
export const REDEMPTION_VOUCHER_CANCELLED = "VOUCHER_CANCELLED" as const;
export const REDEMPTION_VOUCHER_NOT_ISSUED = "VOUCHER_NOT_ISSUED" as const;

// ============================================================
// Phase 10 — Transaction management (void / refund / adjust)
// ============================================================

export const TransactionStatusSchema = z.enum([
  "COMPLETED",
  "VOIDED",
  "REFUNDED",
  "PARTIAL_REFUND",
]);
export type TransactionStatus = z.infer<typeof TransactionStatusSchema>;

export const AdjustmentCurrencySchema = z.enum(["POINTS", "STAMPS"]);
export type AdjustmentCurrency = z.infer<typeof AdjustmentCurrencySchema>;

// Captured at scan time on every `transactions` insert (Phase 5 +
// Phase 8). NULL on rows that pre-date Phase 10 — the void/refund
// engine documents its fallback path against the live tenant rule.
export const LoyaltyCreditSnapshotSchema = z.object({
  pointsCredited: z.number().multipleOf(0.01).nonnegative(),
  stampsCredited: z.number().int().nonnegative(),
  visitCounted: z.number().int().min(0).max(1),
  scanMode: z.enum(["PER_TRANSACTION", "VISIT_ONLY"]),
});
export type LoyaltyCreditSnapshot = z.infer<typeof LoyaltyCreditSnapshotSchema>;

// Tenant-facing transaction view. Customer-facing activity rendering
// reuses `status` + `amount` + `refundedAmount` only — never
// `voidReason` / `refundReason` (audit fields, staff-only).
export const TransactionResponseSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  customerId: z.string().uuid(),
  amount: z.string().nullable(),
  qrNonce: z.string(),
  status: TransactionStatusSchema,
  voidedAt: z.string().datetime().nullable(),
  voidReason: z.string().nullable(),
  refundedAmount: z.string(),
  refundedAt: z.string().datetime().nullable(),
  refundReason: z.string().nullable(),
  pointsReversed: z.number().multipleOf(0.01),
  stampsReversed: z.number().int(),
  loyaltyCreditSnapshot: LoyaltyCreditSnapshotSchema.nullable(),
  createdAt: z.string().datetime(),
});
export type TransactionResponse = z.infer<typeof TransactionResponseSchema>;

// POST /tenant/transactions/:id/void
export const VoidRequestSchema = z.object({
  reason: z.string().trim().min(5, "Reason must be at least 5 characters."),
});
export type VoidRequest = z.infer<typeof VoidRequestSchema>;

// POST /tenant/transactions/:id/refund
//   amount in pesos as a string with up to 2 decimals
//   reason ≥ 5 chars
export const RefundRequestSchema = z.object({
  amount: z
    .string()
    .regex(/^\d+(\.\d{1,2})?$/, "Amount must be a peso value like 250 or 250.50."),
  reason: z.string().trim().min(5, "Reason must be at least 5 characters."),
});
export type RefundRequest = z.infer<typeof RefundRequestSchema>;

export const RefundResponseSchema = z.object({
  transaction: TransactionResponseSchema,
  newPointsBalance: z.number().multipleOf(0.01).nonnegative(),
  newStampsCount: z.number().int().nonnegative(),
  // false for VISIT_ONLY scans (financial reversal only) and for
  // amount-NULL legacy rows (fully blocked at request time).
  loyaltyAdjusted: z.boolean(),
});
export type RefundResponse = z.infer<typeof RefundResponseSchema>;

export const VoidResponseSchema = z.object({
  transaction: TransactionResponseSchema,
  newPointsBalance: z.number().multipleOf(0.01).nonnegative(),
  newStampsCount: z.number().int().nonnegative(),
});
export type VoidResponse = z.infer<typeof VoidResponseSchema>;

// POST /tenant/customers/:tcId/adjust
export const AdjustRequestSchema = z.object({
  delta: z
    .number()
    .multipleOf(0.01)
    .refine((n) => n !== 0, { message: "Delta must be non-zero." }),
  currency: AdjustmentCurrencySchema,
  reason: z.string().trim().min(5, "Reason must be at least 5 characters."),
});
export type AdjustRequest = z.infer<typeof AdjustRequestSchema>;

export const AdjustmentResponseSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  tenantCustomerId: z.string().uuid(),
  delta: z.number().multipleOf(0.01),
  currency: AdjustmentCurrencySchema,
  reason: z.string(),
  actor: z.string(),
  createdAt: z.string().datetime(),
});
export type AdjustmentResponse = z.infer<typeof AdjustmentResponseSchema>;

export const AdjustResponseSchema = z.object({
  adjustment: AdjustmentResponseSchema,
  newPointsBalance: z.number().multipleOf(0.01).nonnegative(),
  newStampsCount: z.number().int().nonnegative(),
});
export type AdjustResponse = z.infer<typeof AdjustResponseSchema>;

// Phase 10 error codes — surfaced in the envelope `error.code` field so
// the merchant UI can branch on machine-readable reasons instead of
// string-matching the message.
export const TX_ERROR_NOT_FOUND = "TRANSACTION_NOT_FOUND" as const;
export const TX_ERROR_TOO_OLD_TO_VOID = "TRANSACTION_TOO_OLD_TO_VOID" as const;
export const TX_ERROR_VOID_AFTER_REFUND = "VOID_AFTER_REFUND_NOT_ALLOWED" as const;
export const TX_ERROR_ALREADY_REFUNDED = "ALREADY_FULLY_REFUNDED" as const;
export const TX_ERROR_NOT_REFUNDABLE_NO_AMOUNT =
  "NOT_REFUNDABLE_NO_AMOUNT" as const;
export const TX_ERROR_REFUND_EXCEEDS_REMAINING =
  "REFUND_EXCEEDS_REMAINING" as const;
export const TX_ERROR_REFUND_VOIDED = "CANNOT_REFUND_VOIDED" as const;
export const ADJ_ERROR_DAILY_LIMIT_EXCEEDED = "DAILY_LIMIT_EXCEEDED" as const;
export const ADJ_ERROR_INSUFFICIENT_BALANCE = "INSUFFICIENT_BALANCE" as const;
export const ADJ_ERROR_TENANT_CUSTOMER_NOT_FOUND =
  "TENANT_CUSTOMER_NOT_FOUND" as const;
export const ADJ_ERROR_CURRENCY_MISMATCH = "CURRENCY_MODE_MISMATCH" as const;

export const DailyLimitExceededDetailsSchema = z.object({
  dailyLimit: z.number().int().nonnegative(),
  alreadyUsed: z.number().int().nonnegative(),
  requested: z.number().int().positive(),
});
export type DailyLimitExceededDetails = z.infer<
  typeof DailyLimitExceededDetailsSchema
>;

// =====================================================================
// Phase 11 — merchant Transactions + Customers list pages
// =====================================================================

// Status filter is repeatable: `?status=COMPLETED&status=VOIDED`. We
// coerce a single value to an array so callers can also send one.
export const TenantTransactionsFeedQuerySchema = z.object({
  status: z
    .union([
      z.array(TransactionStatusSchema),
      TransactionStatusSchema.transform((v) => [v]),
    ])
    .optional(),
  from: z.string().datetime({ offset: true }).optional(),
  to: z.string().datetime({ offset: true }).optional(),
  customerSearch: z.string().trim().min(1).max(64).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  cursor: z.string().min(1).optional(),
});
export type TenantTransactionsFeedQuery = z.infer<
  typeof TenantTransactionsFeedQuerySchema
>;

export const TenantTransactionFeedItemSchema = z.object({
  id: z.string().uuid(),
  customerId: z.string().uuid(),
  customerName: z.string(),
  customerPhoneLast4: z.string().regex(/^\d{4}$/u),
  amount: z.string().nullable(),
  status: TransactionStatusSchema,
  refundedAmount: z.string().nullable(),
  pointsReversed: z.number().multipleOf(0.01).nonnegative(),
  stampsReversed: z.number().int().nonnegative(),
  loyaltyCreditSnapshot: LoyaltyCreditSnapshotSchema.nullable(),
  createdAt: z.string().datetime(),
});
export type TenantTransactionFeedItem = z.infer<
  typeof TenantTransactionFeedItemSchema
>;

export const TenantTransactionsFeedResponseSchema = z.object({
  items: z.array(TenantTransactionFeedItemSchema),
  nextCursor: z.string().nullable(),
});
export type TenantTransactionsFeedResponse = z.infer<
  typeof TenantTransactionsFeedResponseSchema
>;

export const TenantCustomersFeedQuerySchema = z.object({
  search: z.string().trim().min(1).max(64).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  cursor: z.string().min(1).optional(),
});
export type TenantCustomersFeedQuery = z.infer<
  typeof TenantCustomersFeedQuerySchema
>;

export const TenantCustomerListItemSchema = z.object({
  tenantCustomerId: z.string().uuid(),
  customerId: z.string().uuid(),
  name: z.string(),
  phoneLast4: z.string().regex(/^\d{4}$/u),
  // Reuse the canonical tenant-customer status enum from Phase 2 so
  // the merchant-facing list response stays in lock-step with the
  // DB enum (`ACTIVE | CHURNED | BANNED | PAUSED`). Earlier drafts
  // used a UI-friendly `INACTIVE | BLOCKED` triplet which would have
  // hard-failed the outbound `.parse()` for any non-ACTIVE row.
  status: TenantCustomerStatusSchema,
  pointsBalance: z.number().multipleOf(0.01).nonnegative(),
  stampsCount: z.number().int().nonnegative(),
  totalVisits: z.number().int().nonnegative(),
  lastVisitAt: z.string().datetime().nullable(),
  joinedAt: z.string().datetime(),
});
export type TenantCustomerListItem = z.infer<
  typeof TenantCustomerListItemSchema
>;

export const TenantCustomersFeedResponseSchema = z.object({
  items: z.array(TenantCustomerListItemSchema),
  nextCursor: z.string().nullable(),
});
export type TenantCustomersFeedResponse = z.infer<
  typeof TenantCustomersFeedResponseSchema
>;

// =====================================================================
// Phase 12 — Customer profile + connected merchants management
// =====================================================================
//
// All schemas below back the customer-authenticated PWA surface
// mounted at /api/v1/customer/me/* in routes-phase12.ts. Wire format
// invariants:
//   • Phones: PhoneSchema (E.164 PH, +639XXXXXXXXX).
//   • Emails: lowercase server-side; UI sends mixed case.
//   • Timestamps: ISO-8601 with timezone (date-time).
//   • Cursors: opaque strings; consumers MUST NOT parse them.
// Outbound `phone` / `email` are ALWAYS masked when echoing the
// customer's own contact info; the unmasked value lives only in
// CustomerDataExport (which the customer themselves is downloading).

// ----- Language preference (canonical for Phase 12 UI) --------------
//
// The customers.preferred_language column is constrained to this set
// by migration 013. Legacy 'taglish' rows are backfilled to 'en-PH'.
export const LanguageCodeSchema = z.enum(["en-PH", "fil-PH", "tl-PH"]);
export type LanguageCode = z.infer<typeof LanguageCodeSchema>;

// ----- Profile ------------------------------------------------------
//
// GET /api/v1/customer/me/profile
//
// Superset of CustomerMeResponse — adds languagePreference,
// profilePhotoUrl, createdAt, plus the deletion-grace-period
// timestamps so the PWA can render the "your account is scheduled
// for deletion" banner from a single fetch.
// Migration 031 — optional self-serve profile fields. All nullable;
// `gender` is constrained at the API + DB to a small enum.
export const CustomerGenderSchema = z.enum([
  "FEMALE",
  "MALE",
  "OTHER",
  "PREFER_NOT_TO_SAY",
]);
export type CustomerGender = z.infer<typeof CustomerGenderSchema>;

export const CustomerProfileResponseSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  phone: z.string(), // masked: "+63 *** *** 4567"
  birthday: z.string().nullable(), // "YYYY-MM-DD"
  languagePreference: LanguageCodeSchema,
  email: z.string().nullable(), // masked: "m***a@example.com"
  phoneVerified: z.boolean(),
  emailVerified: z.boolean(),
  profilePhotoUrl: z.string().url().nullable(),
  createdAt: z.string().datetime(),
  // Migration 031 — optional profile fields. All nullable.
  gender: CustomerGenderSchema.nullable(),
  addressLine: z.string().nullable(),
  city: z.string().nullable(),
  province: z.string().nullable(),
  postalCode: z.string().nullable(),
  occupation: z.string().nullable(),
  // Soft-delete grace-period state. Both NULL when no deletion is
  // pending. Both set when a deletion has been requested:
  //   deletionRequestedAt    = when the customer asked
  //   scheduledCompletionAt  = deletionRequestedAt + 7 days
  // The PWA renders the banner whenever deletionRequestedAt is set.
  deletionRequestedAt: z.string().datetime().nullable(),
  scheduledCompletionAt: z.string().datetime().nullable(),
});
export type CustomerProfileResponse = z.infer<typeof CustomerProfileResponseSchema>;

// PATCH /api/v1/customer/me/profile
//
// All fields optional; the handler enforces "at least one field
// present" so an empty body returns 400 instead of silently no-op'ing.
// Birthday accepts a YYYY-MM-DD string OR explicit null (clear it).
// profilePhotoUrl accepts a URL or explicit null (remove the photo).
// Helper: a free-text optional profile field. Trim+max+nullable.
// Empty string after trim is coerced to null (the form posts "" when
// the user clears a field, but we want NULL in the DB).
const optionalShortText = (max: number) =>
  z
    .union([
      z
        .string()
        .trim()
        .max(max)
        .transform((s) => (s.length === 0 ? null : s)),
      z.null(),
    ])
    .optional();

export const CustomerProfileUpdateRequestSchema = z
  .object({
    name: z.string().trim().min(1).max(120).optional(),
    birthday: z
      .union([z.string().regex(/^\d{4}-\d{2}-\d{2}$/u), z.null()])
      .optional(),
    languagePreference: LanguageCodeSchema.optional(),
    profilePhotoUrl: z.union([z.string().url(), z.null()]).optional(),
    // Migration 031 — optional profile fields.
    gender: z.union([CustomerGenderSchema, z.null()]).optional(),
    addressLine: optionalShortText(200),
    city: optionalShortText(80),
    province: optionalShortText(80),
    postalCode: optionalShortText(16),
    occupation: optionalShortText(80),
  })
  .refine(
    (v) =>
      v.name !== undefined ||
      v.birthday !== undefined ||
      v.languagePreference !== undefined ||
      v.profilePhotoUrl !== undefined ||
      v.gender !== undefined ||
      v.addressLine !== undefined ||
      v.city !== undefined ||
      v.province !== undefined ||
      v.postalCode !== undefined ||
      v.occupation !== undefined,
    { message: "At least one field is required." },
  );
export type CustomerProfileUpdateRequest = z.infer<
  typeof CustomerProfileUpdateRequestSchema
>;

// ----- Phone change (two-OTP) ---------------------------------------
//
// State machine (see schema.ts customerPhoneChangeStepSchema):
//   AWAITING_OLD_PHONE_OTP → AWAITING_NEW_PHONE_OTP → COMPLETED
//   any-step → CANCELLED  (customer hits "Cancel")
//   any-step → EXPIRED    (server: now > expires_at)
//
// All endpoints take the changeRequestId from the start response so
// the wizard can survive a page reload without re-issuing OTPs.
export const CustomerPhoneChangeStepSchema = z.enum([
  "AWAITING_OLD_PHONE_OTP",
  "AWAITING_NEW_PHONE_OTP",
  "COMPLETED",
  "CANCELLED",
  "EXPIRED",
]);
export type CustomerPhoneChangeStep = z.infer<typeof CustomerPhoneChangeStepSchema>;

// POST /api/v1/customer/me/phone-change/start
export const PhoneChangeStartRequestSchema = z.object({
  newPhone: PhoneSchema,
});
export type PhoneChangeStartRequest = z.infer<typeof PhoneChangeStartRequestSchema>;

export const PhoneChangeStartResponseSchema = z.object({
  changeRequestId: z.string().uuid(),
  oldPhoneOtpRequestId: z.string().uuid(),
  expiresAt: z.string().datetime(),
  maskedOldPhone: z.string(),
  maskedNewPhone: z.string(),
});
export type PhoneChangeStartResponse = z.infer<typeof PhoneChangeStartResponseSchema>;

// POST /api/v1/customer/me/phone-change/verify-old
export const PhoneChangeVerifyOldRequestSchema = z.object({
  changeRequestId: z.string().uuid(),
  code: z.string().regex(/^\d{6}$/u),
});
export type PhoneChangeVerifyOldRequest = z.infer<
  typeof PhoneChangeVerifyOldRequestSchema
>;

export const PhoneChangeVerifyOldResponseSchema = z.object({
  changeRequestId: z.string().uuid(),
  newPhoneOtpRequestId: z.string().uuid(),
  expiresAt: z.string().datetime(),
  maskedNewPhone: z.string(),
});
export type PhoneChangeVerifyOldResponse = z.infer<
  typeof PhoneChangeVerifyOldResponseSchema
>;

// POST /api/v1/customer/me/phone-change/verify-new
export const PhoneChangeVerifyNewRequestSchema = z.object({
  changeRequestId: z.string().uuid(),
  code: z.string().regex(/^\d{6}$/u),
});
export type PhoneChangeVerifyNewRequest = z.infer<
  typeof PhoneChangeVerifyNewRequestSchema
>;

export const PhoneChangeVerifyNewResponseSchema = z.object({
  changeRequestId: z.string().uuid(),
  newPhone: z.string(), // masked, like the rest of the surface
  completedAt: z.string().datetime(),
});
export type PhoneChangeVerifyNewResponse = z.infer<
  typeof PhoneChangeVerifyNewResponseSchema
>;

// POST /api/v1/customer/me/phone-change/cancel
export const PhoneChangeCancelRequestSchema = z.object({
  changeRequestId: z.string().uuid(),
});
export type PhoneChangeCancelRequest = z.infer<typeof PhoneChangeCancelRequestSchema>;

export const PhoneChangeCancelResponseSchema = z.object({
  cancelled: z.literal(true),
});
export type PhoneChangeCancelResponse = z.infer<
  typeof PhoneChangeCancelResponseSchema
>;

// ----- Email change (single OTP delivered to the new address) -------
//
// D32: code-by-email instead of magic-link, to reuse the existing
// email-verification path; see DECISIONS.md.
export const CustomerEmailChangeStepSchema = z.enum([
  "AWAITING_VERIFICATION",
  "COMPLETED",
  "CANCELLED",
  "EXPIRED",
]);
export type CustomerEmailChangeStep = z.infer<typeof CustomerEmailChangeStepSchema>;

// POST /api/v1/customer/me/email-change/start
export const EmailChangeStartRequestSchema = z.object({
  newEmail: z.string().email().max(254),
});
export type EmailChangeStartRequest = z.infer<typeof EmailChangeStartRequestSchema>;

export const EmailChangeStartResponseSchema = z.object({
  changeRequestId: z.string().uuid(),
  otpRequestId: z.string().uuid(),
  expiresAt: z.string().datetime(),
  maskedNewEmail: z.string(),
});
export type EmailChangeStartResponse = z.infer<typeof EmailChangeStartResponseSchema>;

// POST /api/v1/customer/me/email-change/verify
export const EmailChangeVerifyRequestSchema = z.object({
  changeRequestId: z.string().uuid(),
  code: z.string().regex(/^\d{6}$/u),
});
export type EmailChangeVerifyRequest = z.infer<typeof EmailChangeVerifyRequestSchema>;

export const EmailChangeVerifyResponseSchema = z.object({
  changeRequestId: z.string().uuid(),
  newEmail: z.string(), // masked
  completedAt: z.string().datetime(),
});
export type EmailChangeVerifyResponse = z.infer<typeof EmailChangeVerifyResponseSchema>;

// POST /api/v1/customer/me/email-change/cancel
export const EmailChangeCancelRequestSchema = z.object({
  changeRequestId: z.string().uuid(),
});
export type EmailChangeCancelRequest = z.infer<typeof EmailChangeCancelRequestSchema>;

export const EmailChangeCancelResponseSchema = z.object({
  cancelled: z.literal(true),
});
export type EmailChangeCancelResponse = z.infer<typeof EmailChangeCancelResponseSchema>;

// ----- Connected merchants ------------------------------------------
//
// GET /api/v1/customer/me/merchants
//
// One row per tenant_customer (any status). marketingOptIn reflects
// the LATEST consent_log row for (customerId, tenantId, consentType=
// MARKETING_SMS|MARKETING_EMAIL); if no row exists, defaults to false.
// m42MarketingOptIn is the platform-wide variant (consent_log row
// with tenantId IS NULL).
export const MarketingChannelOptInSchema = z.object({
  sms: z.boolean(),
  email: z.boolean(),
});
export type MarketingChannelOptIn = z.infer<typeof MarketingChannelOptInSchema>;

export const M42MarketingOptInSchema = z.object({
  sms: z.boolean(),
  email: z.boolean(),
  push: z.boolean(),
});
export type M42MarketingOptIn = z.infer<typeof M42MarketingOptInSchema>;

export const ConnectedMerchantSchema = z.object({
  tenantCustomerId: z.string().uuid(),
  tenantId: z.string().uuid(),
  tenantName: z.string(),
  verticalOs: VerticalOsSchema,
  loyaltyMode: LoyaltyModeSchema,
  joinedAt: z.string().datetime(),
  lastVisitAt: z.string().datetime().nullable(),
  totalVisits: z.number().int().nonnegative(),
  pointsBalance: z.number().multipleOf(0.01).nonnegative(),
  stampsCount: z.number().int().nonnegative(),
  status: TenantCustomerStatusSchema,
  revokedAt: z.string().datetime().nullable(),
  marketingOptIn: MarketingChannelOptInSchema,
});
export type ConnectedMerchant = z.infer<typeof ConnectedMerchantSchema>;

export const ConnectedMerchantsResponseSchema = z.object({
  merchants: z.array(ConnectedMerchantSchema),
  m42MarketingOptIn: M42MarketingOptInSchema,
});
export type ConnectedMerchantsResponse = z.infer<
  typeof ConnectedMerchantsResponseSchema
>;

// POST /api/v1/customer/me/merchants/:tenantCustomerId/revoke
export const RevokeMerchantRequestSchema = z.object({
  reason: z.string().trim().max(500).optional(),
});
export type RevokeMerchantRequest = z.infer<typeof RevokeMerchantRequestSchema>;

export const RevokeMerchantResponseSchema = z.object({
  tenantCustomerId: z.string().uuid(),
  status: TenantCustomerStatusSchema, // always 'REVOKED' on success
  revokedAt: z.string().datetime(),
});
export type RevokeMerchantResponse = z.infer<typeof RevokeMerchantResponseSchema>;

// PUT /api/v1/customer/me/merchants/:tenantCustomerId/marketing
//
// Toggles a per-tenant marketing channel for this customer. Updates
// consent_log with a fresh row (granted=true|false, consentType=
// MARKETING_SMS|MARKETING_EMAIL). The PWA reads back via the next
// GET /merchants — there's no separate fetch endpoint for the
// per-tenant value.
export const MarketingChannelSchema = z.enum(["SMS", "EMAIL"]);
export type MarketingChannel = z.infer<typeof MarketingChannelSchema>;

export const TenantMarketingToggleRequestSchema = z.object({
  channel: MarketingChannelSchema,
  granted: z.boolean(),
});
export type TenantMarketingToggleRequest = z.infer<
  typeof TenantMarketingToggleRequestSchema
>;

export const TenantMarketingToggleResponseSchema = z.object({
  tenantCustomerId: z.string().uuid(),
  marketingOptIn: MarketingChannelOptInSchema,
});
export type TenantMarketingToggleResponse = z.infer<
  typeof TenantMarketingToggleResponseSchema
>;

// PUT /api/v1/customer/me/marketing/m42
//
// Platform-wide M42 marketing toggle. consent_log row is written
// with tenantId=NULL.
export const M42MarketingChannelSchema = z.enum(["SMS", "EMAIL", "PUSH"]);
export type M42MarketingChannel = z.infer<typeof M42MarketingChannelSchema>;

export const M42MarketingToggleRequestSchema = z.object({
  channel: M42MarketingChannelSchema,
  granted: z.boolean(),
});
export type M42MarketingToggleRequest = z.infer<typeof M42MarketingToggleRequestSchema>;

export const M42MarketingToggleResponseSchema = z.object({
  m42MarketingOptIn: M42MarketingOptInSchema,
});
export type M42MarketingToggleResponse = z.infer<
  typeof M42MarketingToggleResponseSchema
>;

// ----- Consent history ----------------------------------------------
//
// GET /api/v1/customer/me/consents
//
// Cursor-paginated, most-recent-first. The cursor is an opaque
// string composed of (grantedAt-ms, id) — same pattern as Phase 11.
// Items echo the lifecycle audit values (PROFILE_UPDATED,
// PHONE_CHANGED, etc.) alongside the original consent toggles.
export const ConsentHistoryItemSchema = z.object({
  id: z.string().uuid(),
  type: z.string(), // mirrors consent_type enum (text on the wire so new values added later don't trip the parser on old clients)
  granted: z.boolean(),
  grantedAt: z.string().datetime(),
  grantedVia: z.string(),
  documentVersion: z.string(),
  tenantId: z.string().uuid().nullable(),
  tenantName: z.string().nullable(),
  metadata: z.record(z.string(), z.unknown()).nullable(),
});
export type ConsentHistoryItem = z.infer<typeof ConsentHistoryItemSchema>;

export const ConsentHistoryQuerySchema = z.object({
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(50).default(20),
});
export type ConsentHistoryQuery = z.infer<typeof ConsentHistoryQuerySchema>;

export const ConsentHistoryResponseSchema = z.object({
  items: z.array(ConsentHistoryItemSchema),
  nextCursor: z.string().nullable(),
});
export type ConsentHistoryResponse = z.infer<typeof ConsentHistoryResponseSchema>;

// ----- Data export --------------------------------------------------
//
// GET /api/v1/customer/me/data-export
//
// Synchronous JSON download (D33 / DECISIONS.md). The handler sets
// Content-Disposition: attachment so the browser saves a file. The
// shape below is the parsed body; consumers can use it for typing
// when they re-import a downloaded file but the wire response itself
// is application/json.
export const CustomerDataExportSchema = z.object({
  exportedAt: z.string().datetime(),
  schemaVersion: z.literal(1),
  profile: z.object({
    id: z.string().uuid(),
    name: z.string(),
    phone: z.string(), // unmasked — customer's own data
    birthday: z.string().nullable(),
    languagePreference: LanguageCodeSchema,
    email: z.string().nullable(),
    phoneVerified: z.boolean(),
    emailVerified: z.boolean(),
    createdAt: z.string().datetime(),
  }),
  merchants: z.array(
    z.object({
      tenantId: z.string().uuid(),
      tenantName: z.string(),
      joinedAt: z.string().datetime(),
      status: TenantCustomerStatusSchema,
      revokedAt: z.string().datetime().nullable(),
      totalVisits: z.number().int().nonnegative(),
      totalSpend: z.string(), // numeric(12,2) as string
      pointsBalance: z.number().multipleOf(0.01).nonnegative(),
      stampsCount: z.number().int().nonnegative(),
    }),
  ),
  transactions: z.array(
    z.object({
      id: z.string().uuid(),
      tenantId: z.string().uuid(),
      tenantName: z.string(),
      amount: z.string().nullable(),
      status: z.string(),
      createdAt: z.string().datetime(),
    }),
  ),
  redemptions: z.array(
    z.object({
      id: z.string().uuid(),
      tenantId: z.string().uuid(),
      tenantName: z.string(),
      voucherCode: z.string(),
      rewardName: z.string().nullable(),
      status: z.string(),
      pointsSpent: z.number().multipleOf(0.01).nullable(),
      stampsSpent: z.number().int().nullable(),
      issuedAt: z.string().datetime(),
      redeemedAt: z.string().datetime().nullable(),
      cancelledAt: z.string().datetime().nullable(),
    }),
  ),
  adjustments: z.array(
    z.object({
      id: z.string().uuid(),
      tenantId: z.string().uuid(),
      tenantName: z.string(),
      delta: z.number().multipleOf(0.01),
      currency: z.string(),
      reason: z.string(),
      createdAt: z.string().datetime(),
    }),
  ),
  consents: z.array(ConsentHistoryItemSchema),
  // Phase 26 (D92) — additive extension. Always present; empty array
  // for customers with no registered vehicles. The inline shape avoids
  // a forward reference to CustomerVehicleExportItemSchema (defined
  // further down in the Phase 26 block) and keeps this section
  // self-contained.
  vehicles: z.array(
    z.object({
      plateNumber: z.string(),
      nickname: z.string().nullable(),
      vehicleSize: z
        .enum(["SMALL", "MEDIUM", "LARGE", "EXTRA_LARGE"])
        .nullable(),
      addedAt: z.string().datetime(),
    }),
  ),
});
export type CustomerDataExport = z.infer<typeof CustomerDataExportSchema>;

// ----- Account deletion --------------------------------------------
//
// POST /api/v1/customer/me/account-delete/request
//
// Re-prompts the customer's password before scheduling deletion so
// a stolen / left-open session can't nuke an account. The handler
// uses the Phase 7 bcrypt verifier on customers.password_hash.
export const AccountDeleteRequestRequestSchema = z.object({
  password: z.string().min(1),
  reason: z.string().trim().max(500).optional(),
});
export type AccountDeleteRequestRequest = z.infer<
  typeof AccountDeleteRequestRequestSchema
>;

export const AccountDeleteRequestResponseSchema = z.object({
  deletionRequestedAt: z.string().datetime(),
  scheduledCompletionAt: z.string().datetime(),
});
export type AccountDeleteRequestResponse = z.infer<
  typeof AccountDeleteRequestResponseSchema
>;

// POST /api/v1/customer/me/account-delete/cancel
export const AccountDeleteCancelResponseSchema = z.object({
  cancelled: z.literal(true),
  deletionRequestedAt: z.null(),
});
export type AccountDeleteCancelResponse = z.infer<
  typeof AccountDeleteCancelResponseSchema
>;

// GET /api/v1/customer/me/account-delete/status
export const AccountDeleteStatusResponseSchema = z.object({
  deletionRequestedAt: z.string().datetime().nullable(),
  scheduledCompletionAt: z.string().datetime().nullable(),
});
export type AccountDeleteStatusResponse = z.infer<
  typeof AccountDeleteStatusResponseSchema
>;

// =====================================================================
// Phase 13 — Merchant Dashboard + Customer 360 + List filters
// =====================================================================

// ----- Dashboard / today stats -----

export const TenantDashboardTodayResponseSchema = z.object({
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/u),
  transactions: z.object({
    completed: z.number().int().nonnegative(),
    voided: z.number().int().nonnegative(),
    refunded: z.number().int().nonnegative(),
  }),
  revenuePeso: z.string(),
  visitOnlyTransactions: z.number().int().nonnegative(),
  uniqueCustomers: z.number().int().nonnegative(),
  loyalty: z.object({
    pointsCredited: z.number().multipleOf(0.01),
    stampsCredited: z.number().int(),
  }),
  redemptions: z.object({
    issued: z.number().int().nonnegative(),
    redeemed: z.number().int().nonnegative(),
  }),
  adjustments: z.object({
    count: z.number().int().nonnegative(),
    totalDeltaPoints: z.number().multipleOf(0.01),
    totalDeltaStamps: z.number().int(),
  }),
});
export type TenantDashboardTodayResponse = z.infer<
  typeof TenantDashboardTodayResponseSchema
>;

// ----- Recent activity feed -----

export const RecentActivityKindSchema = z.enum([
  "TRANSACTION_COMPLETED",
  "TRANSACTION_VOIDED",
  "TRANSACTION_REFUNDED",
  "REDEMPTION_ISSUED",
  "REDEMPTION_REDEEMED",
  "ADJUSTMENT",
  "CUSTOMER_REVOKED",
  "CUSTOMER_RE_ENROLLED",
]);
export type RecentActivityKind = z.infer<typeof RecentActivityKindSchema>;

export const RecentActivityItemSchema = z.object({
  kind: RecentActivityKindSchema,
  occurredAt: z.string().datetime(),
  customerId: z.string().uuid(),
  customerName: z.string(),
  customerAnonymized: z.boolean(),
  tenantCustomerId: z.string().uuid().nullable(),
  amount: z.string().nullable(),
  meta: z.record(z.string(), z.unknown()),
  sourceId: z.string().uuid(),
});
export type RecentActivityItem = z.infer<typeof RecentActivityItemSchema>;

export const TenantRecentActivityResponseSchema = z.object({
  items: z.array(RecentActivityItemSchema),
});
export type TenantRecentActivityResponse = z.infer<
  typeof TenantRecentActivityResponseSchema
>;

// ----- Customer-list sort extension -----

export const TenantCustomersListSortSchema = z.enum([
  "recent",
  "top-spenders",
  "lapsing",
]);
export type TenantCustomersListSort = z.infer<
  typeof TenantCustomersListSortSchema
>;

// Phase 13 — extended query schema. Backwards-compatible: omitting
// `sort` and `includeInactive` reproduces Phase 11 default behavior.
export const TenantCustomersFeedQuerySchemaV2 = TenantCustomersFeedQuerySchema.extend({
  sort: TenantCustomersListSortSchema.optional(),
  includeInactive: z
    .union([z.boolean(), z.enum(["true", "false"])])
    .transform((v) => (typeof v === "boolean" ? v : v === "true"))
    .optional(),
});
export type TenantCustomersFeedQueryV2 = z.infer<
  typeof TenantCustomersFeedQuerySchemaV2
>;

// Extends Phase 11's list item with the totalSpend field. Sort=recent
// callers continue to receive null for totalSpend; sort=top-spenders
// and sort=lapsing populate it.
export const TenantCustomerListItemV2Schema = TenantCustomerListItemSchema.extend({
  totalSpend: z.string().nullable(),
});
export type TenantCustomerListItemV2 = z.infer<
  typeof TenantCustomerListItemV2Schema
>;

export const TenantCustomersFeedResponseV2Schema = z.object({
  items: z.array(TenantCustomerListItemV2Schema),
  nextCursor: z.string().nullable(),
});
export type TenantCustomersFeedResponseV2 = z.infer<
  typeof TenantCustomersFeedResponseV2Schema
>;

// ----- Per-customer paginated lists (Customer 360 tabs) -----

export const CustomerTabPageQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).default(25),
  cursor: z.string().min(1).optional(),
});
export type CustomerTabPageQuery = z.infer<typeof CustomerTabPageQuerySchema>;

export const CustomerTabTransactionsQuerySchema =
  CustomerTabPageQuerySchema.extend({
    status: z
      .union([
        z.array(TransactionStatusSchema),
        TransactionStatusSchema.transform((v) => [v]),
      ])
      .optional(),
  });
export type CustomerTabTransactionsQuery = z.infer<
  typeof CustomerTabTransactionsQuerySchema
>;

export const CustomerTransactionItemSchema = z.object({
  id: z.string().uuid(),
  amount: z.string().nullable(),
  status: TransactionStatusSchema,
  refundedAmount: z.string(),
  pointsReversed: z.number().multipleOf(0.01).nonnegative(),
  stampsReversed: z.number().int().nonnegative(),
  loyaltyCreditSnapshot: LoyaltyCreditSnapshotSchema.nullable(),
  createdAt: z.string().datetime(),
  voidedAt: z.string().datetime().nullable(),
  voidReason: z.string().nullable(),
  refundedAt: z.string().datetime().nullable(),
  refundReason: z.string().nullable(),
});
export type CustomerTransactionItem = z.infer<
  typeof CustomerTransactionItemSchema
>;

export const TenantCustomerTransactionsResponseSchema = z.object({
  tenantCustomerId: z.string().uuid(),
  items: z.array(CustomerTransactionItemSchema),
  nextCursor: z.string().nullable(),
});
export type TenantCustomerTransactionsResponse = z.infer<
  typeof TenantCustomerTransactionsResponseSchema
>;

export const CustomerRedemptionItemSchema = z.object({
  id: z.string().uuid(),
  voucherCode: z.string(),
  status: RedemptionStatusSchema,
  rewardName: z.string().nullable(),
  rewardType: z.string().nullable(),
  pointsSpent: z.number().multipleOf(0.01).nullable(),
  stampsSpent: z.number().int().nullable(),
  issuedAt: z.string().datetime(),
  expiresAt: z.string().datetime(),
  redeemedAt: z.string().datetime().nullable(),
});
export type CustomerRedemptionItem = z.infer<
  typeof CustomerRedemptionItemSchema
>;

export const TenantCustomerRedemptionsResponseSchema = z.object({
  tenantCustomerId: z.string().uuid(),
  items: z.array(CustomerRedemptionItemSchema),
  nextCursor: z.string().nullable(),
});
export type TenantCustomerRedemptionsResponse = z.infer<
  typeof TenantCustomerRedemptionsResponseSchema
>;

export const CustomerAdjustmentItemSchema = z.object({
  id: z.string().uuid(),
  delta: z.number().multipleOf(0.01),
  currency: z.enum(["POINTS", "STAMPS"]),
  reason: z.string(),
  actor: z.string(),
  createdAt: z.string().datetime(),
  runningPointsBalance: z.number().multipleOf(0.01).nullable(),
  runningStampsBalance: z.number().int().nullable(),
});
export type CustomerAdjustmentItem = z.infer<
  typeof CustomerAdjustmentItemSchema
>;

export const TenantCustomerAdjustmentsResponseSchema = z.object({
  tenantCustomerId: z.string().uuid(),
  items: z.array(CustomerAdjustmentItemSchema),
  nextCursor: z.string().nullable(),
});
export type TenantCustomerAdjustmentsResponse = z.infer<
  typeof TenantCustomerAdjustmentsResponseSchema
>;

export const CustomerConsentItemSchema = z.object({
  id: z.string().uuid(),
  consentType: z.string(),
  granted: z.boolean(),
  grantedAt: z.string().datetime(),
  revokedAt: z.string().datetime().nullable(),
  grantedVia: z.string(),
  documentVersion: z.string(),
});
export type CustomerConsentItem = z.infer<typeof CustomerConsentItemSchema>;

export const TenantCustomerConsentsResponseSchema = z.object({
  tenantCustomerId: z.string().uuid(),
  items: z.array(CustomerConsentItemSchema),
  nextCursor: z.string().nullable(),
});
export type TenantCustomerConsentsResponse = z.infer<
  typeof TenantCustomerConsentsResponseSchema
>;

// =====================================================================
// Phase 14 — Universal Claim Code (marketplace capture)
// =====================================================================

export const MarketplacePlatformSchema = z.enum([
  "SHOPEE",
  "LAZADA",
  "TIKTOK",
  "OTHER",
]);
export type MarketplacePlatform = z.infer<typeof MarketplacePlatformSchema>;

export const ClaimCodeStatusSchema = z.enum([
  "UNCLAIMED",
  "CLAIMED",
  "EXPIRED",
  "VOIDED",
]);
export type ClaimCodeStatus = z.infer<typeof ClaimCodeStatusSchema>;

export const ClaimAttemptOutcomeSchema = z.enum([
  "SUCCESS",
  "CODE_NOT_FOUND",
  "CODE_ALREADY_CLAIMED",
  "CODE_EXPIRED",
  "CODE_VOIDED",
  "RATE_LIMITED",
  "INVALID_FORMAT",
]);
export type ClaimAttemptOutcome = z.infer<typeof ClaimAttemptOutcomeSchema>;

// ----- POST /tenant/marketplace/upload (multipart) ------------------
//
// The route reads the file from `req.file` (multer); the body of the
// multipart carries the optional `platform` hint as a form field.
export const TenantMarketplaceUploadFormSchema = z.object({
  platform: MarketplacePlatformSchema.optional(),
});

export const TenantMarketplaceUploadResponseSchema = z.object({
  platform: MarketplacePlatformSchema,
  totalRows: z.number().int().nonnegative(),
  ordersAccepted: z.number().int().nonnegative(),
  codesGenerated: z.number().int().nonnegative(),
  skipped: z.object({
    duplicate: z.number().int().nonnegative(),
    cancelled: z.number().int().nonnegative(),
    refunded: z.number().int().nonnegative(),
    invalid: z.number().int().nonnegative(),
  }),
  // Capped at 100 entries — parsers stop appending warnings after this
  // limit so the response payload stays bounded regardless of file size.
  warnings: z.array(z.string()).max(100),
  // Echo back the codes generated in this batch so the merchant can
  // call /generate-pdf for them immediately without a list-fetch.
  // Capped at 2,000 to keep the response payload within reason; large
  // uploads should use GET /codes with pagination to retrieve all codes.
  generatedCodes: z.array(z.string()).max(2000),
});
export type TenantMarketplaceUploadResponse = z.infer<
  typeof TenantMarketplaceUploadResponseSchema
>;

// ----- POST /tenant/marketplace/generate-pdf -----------------------
export const TenantMarketplacePdfRequestSchema = z.object({
  // Either explicit codes (post-upload "print just-generated batch")
  // or a filter (re-print yesterday's UNCLAIMED). At least one of the
  // two must be present.
  // Cap at 2,000 — matches the PDF generator's per-request slip limit.
  // Larger batches must be split across multiple calls.
  codes: z.array(z.string().min(1)).max(2000).optional(),
  filter: z
    .object({
      platform: MarketplacePlatformSchema.optional(),
      status: ClaimCodeStatusSchema.optional(),
      issuedAfter: z.string().datetime().optional(),
      issuedBefore: z.string().datetime().optional(),
    })
    .optional(),
}).refine(
  (v) => (v.codes && v.codes.length > 0) || v.filter !== undefined,
  { message: "Provide either codes or a filter." },
);

// ----- GET /tenant/marketplace/codes -------------------------------
export const TenantMarketplaceCodesQuerySchema = z.object({
  platform: MarketplacePlatformSchema.optional(),
  status: ClaimCodeStatusSchema.optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  cursor: z.string().optional(),
});

export const MarketplaceClaimCodeListItemSchema = z.object({
  id: z.string().uuid(),
  code: z.string(),
  platform: MarketplacePlatformSchema,
  externalOrderId: z.string(),
  externalBuyerHandle: z.string().nullable(),
  grossAmountCentavos: z.number().int().nonnegative(),
  currency: z.string().length(3),
  status: ClaimCodeStatusSchema,
  claimedByCustomerName: z.string().nullable(),
  claimedAt: z.string().datetime().nullable(),
  expiresAt: z.string().datetime(),
  issuedAt: z.string().datetime(),
});
export type MarketplaceClaimCodeListItem = z.infer<
  typeof MarketplaceClaimCodeListItemSchema
>;

export const TenantMarketplaceCodesListResponseSchema = z.object({
  items: z.array(MarketplaceClaimCodeListItemSchema),
  nextCursor: z.string().nullable(),
});

// ----- POST /tenant/marketplace/codes/:code/void ------------------
export const TenantMarketplaceVoidRequestSchema = z.object({
  reason: z.string().min(5).max(500),
});

export const TenantMarketplaceVoidResponseSchema = z.object({
  code: z.string(),
  status: ClaimCodeStatusSchema,
  reversedPoints: z.number().int(),
  reversedStamps: z.number().int(),
});

// ----- GET /customer/claim/:code/preview (public) -----------------
export const CustomerClaimPreviewResponseSchema = z.object({
  code: z.string(),
  platform: MarketplacePlatformSchema,
  tenantName: z.string(),
  grossAmountCentavos: z.number().int().nonnegative(),
  currency: z.string().length(3),
  status: ClaimCodeStatusSchema,
  expiresAt: z.string().datetime(),
  // Hint the client uses to decide which UI branch to show.
  alreadyClaimedByCurrentCustomer: z.boolean(),
});

// ----- POST /customer/claim/:code (auth) --------------------------
export const CustomerClaimResponseSchema = z.object({
  code: z.string(),
  status: ClaimCodeStatusSchema, // CLAIMED on success
  pointsCredited: z.number().multipleOf(0.01).nonnegative(),
  stampsCredited: z.number().int().nonnegative(),
  newPointsBalance: z.number().multipleOf(0.01).nonnegative(),
  newStampsCount: z.number().int().nonnegative(),
  tenantId: z.string().uuid(),
  tenantName: z.string(),
});

// ----- POST /customer/claim/:code/with-signup (public) -----------
export const CustomerClaimWithSignupRequestSchema = z.object({
  phone: z.string().min(7).max(20),
  password: z.string().min(8).max(72),
  name: z.string().min(1).max(120),
  email: z.string().email().nullable().optional(),
});

export const CustomerClaimWithSignupResponseSchema = z.object({
  customerId: z.string().uuid(),
  tokens: z.object({
    accessToken: z.string(),
    refreshToken: z.string(),
    accessTokenExpiresAt: z.string().datetime(),
    refreshTokenExpiresAt: z.string().datetime(),
  }),
  claim: CustomerClaimResponseSchema,
});

// =============================================================================
// Phase 15 — In-app inbox
// =============================================================================
//
// Wire shapes for the customer inbox. The inbox stores typed messages with
// per-`messageType` payloads; the Zod schemas below are the boundary the
// server validates against on write and the client trusts on read.
//
// Discriminated union over `messageType` lets the customer client switch
// directly on the field and render payload-aware CTAs (e.g. "View voucher"
// for VOUCHER_EXPIRING_SOON) without any runtime type-narrowing tricks.
// -----------------------------------------------------------------------------

export const InboxAuthorTypeSchema = z.enum([
  "SYSTEM",
  "TENANT",
  "M42_PLATFORM",
]);
export type InboxAuthorType = z.infer<typeof InboxAuthorTypeSchema>;

export const InboxMessageTypeSchema = z.enum([
  "VOUCHER_EXPIRING_SOON",
  "MARKETPLACE_CLAIM_CREDITED",
  "TENANT_MESSAGE",
  "M42_ANNOUNCEMENT",
  // Phase 19 — Surprise Card. Both ride Phase 18's post-create push
  // hook for free; the schema-side enum (lib/db/src/schema.ts) and
  // the runtime PG enum (migration 022) are kept in lockstep.
  "SURPRISE_AVAILABLE",
  "SURPRISE_CLAIMED",
  // Phase 20 — Streaks & Milestones. Kept in lockstep with the
  // runtime PG enum (migration 024) and the Drizzle enum
  // (lib/db/src/schema.ts L218-219). STREAK_AT_RISK fires from the
  // hourly sweeper (D76); MILESTONE_HIT fires lazily from the
  // post-transaction evaluator (batched per Risk 5, D75).
  "STREAK_AT_RISK",
  "MILESTONE_HIT",
  // Phase 21 — Monthly Recap + Year-in-Review. RECAP_AVAILABLE fires
  // from the eager day-1..7 monthly cron and the admin-triggered
  // Year-in-Review dispatch. Kept in lockstep with migration 025 and
  // the Drizzle enum (lib/db/src/schema.ts).
  "RECAP_AVAILABLE",
]);
export type InboxMessageType = z.infer<typeof InboxMessageTypeSchema>;

export const InboxCategorySchema = z.enum([
  "TRANSACTIONAL",
  "MARKETING",
  "SYSTEM",
]);
export type InboxCategory = z.infer<typeof InboxCategorySchema>;

// ----- Per-`messageType` payload shapes -----------------------------------

export const InboxPayloadVoucherExpiringSchema = z.object({
  voucherId: z.string().uuid(),
  voucherCode: z.string(),
  rewardName: z.string(),
  expiresAt: z.string().datetime(),
});

export const InboxPayloadClaimCreditedSchema = z.object({
  claimCode: z.string(),
  platform: z.enum(["SHOPEE", "LAZADA", "TIKTOK", "OTHER"]),
  tenantName: z.string(),
  pointsCredited: z.number().multipleOf(0.01).nonnegative(),
  stampsCredited: z.number().int().nonnegative(),
  grossAmountCentavos: z.number().int().nonnegative().nullable(),
});

// CTA links must be plain http/https — `javascript:` and `data:` URIs are
// rejected at the API layer so a malicious tenant can't smuggle scripts
// past the markdown renderer. Same gate is applied to the body's
// markdown-link sanitizer client-side; this is the server-side belt.
const SafeUrlSchema = z
  .string()
  .url()
  .refine((u) => /^https?:/i.test(u), {
    message: "URL must be http or https",
  });

export const InboxCtaSchema = z.object({
  ctaUrl: SafeUrlSchema,
  ctaLabel: z.string().min(1).max(40),
});

export const InboxPayloadTenantMessageSchema = InboxCtaSchema.partial()
  .nullable()
  .optional();

export const InboxPayloadM42AnnouncementSchema = InboxCtaSchema.partial()
  .nullable()
  .optional();

// ----- Canonical inbox-message shape returned to the customer client -----

const InboxMessageBaseSchema = z.object({
  id: z.string().uuid(),
  authorType: InboxAuthorTypeSchema,
  authorTenantId: z.string().uuid().nullable(),
  authorTenantName: z.string().nullable(),
  authorAdminId: z.string().uuid().nullable(),
  subject: z.string().min(1).max(100),
  body: z.string().min(1).max(2000),
  category: InboxCategorySchema,
  readAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
});

export const InboxMessageSchema = z.discriminatedUnion("messageType", [
  InboxMessageBaseSchema.extend({
    messageType: z.literal("VOUCHER_EXPIRING_SOON"),
    payload: InboxPayloadVoucherExpiringSchema,
  }),
  InboxMessageBaseSchema.extend({
    messageType: z.literal("MARKETPLACE_CLAIM_CREDITED"),
    payload: InboxPayloadClaimCreditedSchema,
  }),
  InboxMessageBaseSchema.extend({
    messageType: z.literal("TENANT_MESSAGE"),
    payload: InboxPayloadTenantMessageSchema,
  }),
  InboxMessageBaseSchema.extend({
    messageType: z.literal("M42_ANNOUNCEMENT"),
    payload: InboxPayloadM42AnnouncementSchema,
  }),
]);
export type InboxMessage = z.infer<typeof InboxMessageSchema>;

// ----- Customer endpoints ------------------------------------------------

export const CustomerInboxListRequestSchema = z.object({
  cursor: z.string().datetime().optional(),
  limit: z.coerce.number().int().min(1).max(50).optional().default(20),
  unreadOnly: z
    .union([z.boolean(), z.enum(["true", "false"])])
    .optional()
    .transform((v) => v === true || v === "true"),
});

export const CustomerInboxListResponseSchema = z.object({
  items: z.array(InboxMessageSchema),
  nextCursor: z.string().datetime().nullable(),
});
export type CustomerInboxListResponse = z.infer<
  typeof CustomerInboxListResponseSchema
>;

export const CustomerInboxUnreadCountResponseSchema = z.object({
  unreadCount: z.number().int().nonnegative(),
});
export type CustomerInboxUnreadCountResponse = z.infer<
  typeof CustomerInboxUnreadCountResponseSchema
>;

export const CustomerInboxMessageResponseSchema = z.object({
  message: InboxMessageSchema,
});
export type CustomerInboxMessageResponse = z.infer<
  typeof CustomerInboxMessageResponseSchema
>;

export const CustomerInboxMarkAllReadResponseSchema = z.object({
  marked: z.number().int().nonnegative(),
});
export type CustomerInboxMarkAllReadResponse = z.infer<
  typeof CustomerInboxMarkAllReadResponseSchema
>;

// ----- Tenant compose ----------------------------------------------------

/** Phase 39 — channels the merchant can broadcast on. */
export const TenantInboxChannelSchema = z.enum(["INBOX", "EMAIL", "SMS"]);
export type TenantInboxChannel = z.infer<typeof TenantInboxChannelSchema>;

export const TenantInboxComposeRequestSchema = z.object({
  recipientFilter: z.object({
    type: z.literal("ALL_ENROLLED"),
  }),
  subject: z.string().min(1).max(100),
  body: z.string().min(1).max(2000),
  category: z.enum(["TRANSACTIONAL", "MARKETING"]),
  ctaUrl: SafeUrlSchema.optional(),
  ctaLabel: z.string().min(1).max(40).optional(),
  // Phase 39 — defaults to ["INBOX"] for back-compat with the
  // pre-Phase-39 client which never sent this field.
  channels: z.array(TenantInboxChannelSchema).min(1).max(3).optional(),
});
export type TenantInboxComposeRequest = z.infer<
  typeof TenantInboxComposeRequestSchema
>;

export const TenantInboxChannelCountsSchema = z.object({
  attempted: z.number().int().nonnegative(),
  success: z.number().int().nonnegative(),
  skippedMarketingOptOut: z.number().int().nonnegative(),
  skippedRateLimit: z.number().int().nonnegative(),
  skippedNoChannel: z.number().int().nonnegative(),
  skippedNotConfigured: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
});
export type TenantInboxChannelCounts = z.infer<
  typeof TenantInboxChannelCountsSchema
>;

export const TenantInboxComposeResponseSchema = z.object({
  // Back-compat: equals channels.inbox.success.
  sent: z.number().int().nonnegative(),
  skippedMarketingOptOut: z.number().int().nonnegative(),
  skippedRateLimit: z.number().int().nonnegative(),
  skippedNotEnrolled: z.number().int().nonnegative(),
  // Phase 39 — multi-channel detail. Optional on the type so the
  // legacy single-channel response still validates.
  channels: z
    .object({
      inbox: TenantInboxChannelCountsSchema,
      email: TenantInboxChannelCountsSchema,
      sms: TenantInboxChannelCountsSchema,
    })
    .optional(),
  smsCreditsCharged: z.number().int().nonnegative().optional(),
  smsCreditsRemaining: z.number().int().nonnegative().optional(),
});
export type TenantInboxComposeResponse = z.infer<
  typeof TenantInboxComposeResponseSchema
>;

export const TenantInboxSentItemSchema = z.object({
  id: z.string().uuid(),
  subject: z.string(),
  category: InboxCategorySchema,
  recipientCount: z.number().int().nonnegative(),
  sentAt: z.string().datetime(),
});
export type TenantInboxSentItem = z.infer<typeof TenantInboxSentItemSchema>;

export const TenantInboxSentListResponseSchema = z.object({
  items: z.array(TenantInboxSentItemSchema),
  nextCursor: z.string().datetime().nullable(),
});
export type TenantInboxSentListResponse = z.infer<
  typeof TenantInboxSentListResponseSchema
>;

// ----- Admin compose -----------------------------------------------------
//
// Targeting filter is a discriminated union. ALL_CUSTOMERS hits every
// active, non-deleted customer; ENROLLED_WITH_TENANT scopes to a tenant's
// active enrolment list. Deferred for Phase 15: targeting by city
// (customers schema doesn't track city today — see DECISIONS.md).
export const AdminInboxTargetingFilterSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("ALL_CUSTOMERS") }),
  z.object({
    type: z.literal("ENROLLED_WITH_TENANT"),
    tenantId: z.string().uuid(),
  }),
]);

// Phase 35 (D108) — multi-channel platform broadcast.
//
// `channels` is an additive opt-in array. Back-compat: pre-Phase-35
// admin clients omit the field, which `default(["INBOX"])` resolves
// to the legacy in-app-only behaviour. The composer UI renders a
// checkbox per channel; pick any non-empty subset.
export const PlatformMessageChannelSchema = z.enum([
  "INBOX",
  "EMAIL",
  "SMS",
]);
export type PlatformMessageChannel = z.infer<
  typeof PlatformMessageChannelSchema
>;

export const AdminInboxComposeRequestSchema = z.object({
  targetingFilter: AdminInboxTargetingFilterSchema,
  subject: z.string().min(1).max(100),
  body: z.string().min(1).max(2000),
  category: z.enum(["TRANSACTIONAL", "MARKETING"]).default("MARKETING"),
  ctaUrl: SafeUrlSchema.optional(),
  ctaLabel: z.string().min(1).max(40).optional(),
  channels: z
    .array(PlatformMessageChannelSchema)
    .min(1, "Pick at least one channel.")
    .default(["INBOX"]),
});
export type AdminInboxComposeRequest = z.infer<
  typeof AdminInboxComposeRequestSchema
>;

// Per-channel result counters returned from POST /admin/inbox/send.
// `sent` is preserved at the top level for back-compat with pre-
// Phase-35 admin client builds — it equals `inbox.success` (the
// legacy in-app-only count).
export const ChannelDispatchCountsSchema = z.object({
  attempted: z.number().int().nonnegative(),
  success: z.number().int().nonnegative(),
  skipped: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
});
export type ChannelDispatchCounts = z.infer<
  typeof ChannelDispatchCountsSchema
>;

export const AdminInboxComposeResponseSchema = z.object({
  dispatchId: z.string().uuid(),
  recipientCount: z.number().int().nonnegative(),
  sent: z.number().int().nonnegative(),
  channels: z.object({
    inbox: ChannelDispatchCountsSchema,
    email: ChannelDispatchCountsSchema,
    sms: ChannelDispatchCountsSchema,
  }),
});
export type AdminInboxComposeResponse = z.infer<
  typeof AdminInboxComposeResponseSchema
>;

export const AdminInboxSentItemSchema = z.object({
  id: z.string().uuid(),
  subject: z.string(),
  category: InboxCategorySchema,
  recipientCount: z.number().int().nonnegative(),
  sentAt: z.string().datetime(),
});
export type AdminInboxSentItem = z.infer<typeof AdminInboxSentItemSchema>;

export const AdminInboxSentListResponseSchema = z.object({
  items: z.array(AdminInboxSentItemSchema),
  nextCursor: z.string().datetime().nullable(),
});
export type AdminInboxSentListResponse = z.infer<
  typeof AdminInboxSentListResponseSchema
>;

// Dispatch-log surface — paginated history of multi-channel sends
// with pre-aggregated per-channel counts so the admin Dispatch Log
// tab doesn't need a results-table GROUP BY on every page load.
//
// Named `AdminInboxDispatchLog*` (not `AdminDispatchLog*`) to avoid
// collision with the Phase 18 push-dispatch log schemas defined
// further down in this file.
export const AdminInboxDispatchLogItemSchema = z.object({
  id: z.string().uuid(),
  subject: z.string(),
  category: InboxCategorySchema,
  targetingType: z.enum(["ALL_CUSTOMERS", "ENROLLED_WITH_TENANT"]),
  targetingTenantId: z.string().uuid().nullable(),
  targetingTenantName: z.string().nullable(),
  channels: z.array(PlatformMessageChannelSchema),
  recipientCount: z.number().int().nonnegative(),
  counts: z.object({
    inbox: ChannelDispatchCountsSchema,
    email: ChannelDispatchCountsSchema,
    sms: ChannelDispatchCountsSchema,
  }),
  sentAt: z.string().datetime(),
});
export type AdminInboxDispatchLogItem = z.infer<
  typeof AdminInboxDispatchLogItemSchema
>;

export const AdminInboxDispatchLogResponseSchema = z.object({
  items: z.array(AdminInboxDispatchLogItemSchema),
  nextCursor: z.string().datetime().nullable(),
});
export type AdminInboxDispatchLogResponse = z.infer<
  typeof AdminInboxDispatchLogResponseSchema
>;

// Pre-send eligibility estimate. Powers the confirmation modal so the
// admin sees how many recipients each chosen channel will actually
// reach (after no-contact / opt-out / sender-not-configured filtering)
// before committing the broadcast. Mirrors the dispatcher's gating
// logic so modal numbers can't drift from real send outcomes.
export const AdminInboxEstimateRequestSchema = z.object({
  targetingFilter: AdminInboxTargetingFilterSchema,
  category: z.enum(["TRANSACTIONAL", "MARKETING"]),
  channels: z
    .array(PlatformMessageChannelSchema)
    .min(1, "Pick at least one channel."),
});
export type AdminInboxEstimateRequest = z.infer<
  typeof AdminInboxEstimateRequestSchema
>;

export const AdminInboxEstimateResponseSchema = z.object({
  recipientCount: z.number().int().nonnegative(),
  channels: z.object({
    inbox: z.object({ eligible: z.number().int().nonnegative() }),
    email: z.object({
      eligible: z.number().int().nonnegative(),
      missingChannel: z.number().int().nonnegative(),
      optedOut: z.number().int().nonnegative(),
      notConfigured: z.boolean(),
    }),
    sms: z.object({
      eligible: z.number().int().nonnegative(),
      missingChannel: z.number().int().nonnegative(),
      optedOut: z.number().int().nonnegative(),
      notConfigured: z.boolean(),
    }),
  }),
});
export type AdminInboxEstimateResponse = z.infer<
  typeof AdminInboxEstimateResponseSchema
>;

// Per-recipient × per-channel forensics for the dispatch detail page.
// `contact` is always masked (last-4 / partial local-part) to keep the
// admin log from being a casual PII export. Full PII still lives behind
// the existing admin Customers tools.
export const AdminInboxDispatchDetailResultSchema = z.object({
  customerId: z.string().uuid(),
  contact: z.string().nullable(),
  outcome: z.enum([
    "SUCCESS",
    "SKIPPED_NO_CHANNEL",
    "SKIPPED_OPTOUT",
    "SKIPPED_NOT_CONFIGURED",
    "FAILED",
  ]),
  errorMessage: z.string().nullable(),
});
export type AdminInboxDispatchDetailResult = z.infer<
  typeof AdminInboxDispatchDetailResultSchema
>;

export const AdminInboxDispatchDetailResponseSchema =
  AdminInboxDispatchLogItemSchema.extend({
    body: z.string(),
    ctaUrl: z.string().nullable(),
    ctaLabel: z.string().nullable(),
    results: z.object({
      inbox: z.array(AdminInboxDispatchDetailResultSchema),
      email: z.array(AdminInboxDispatchDetailResultSchema),
      sms: z.array(AdminInboxDispatchDetailResultSchema),
    }),
  });
export type AdminInboxDispatchDetailResponse = z.infer<
  typeof AdminInboxDispatchDetailResponseSchema
>;

// ============================================================================
// Phase 17 — tenant token recovery (D53)
// ============================================================================
// All four public recovery endpoints live under /api/v1/merchant/recover/*
// and are unauthenticated. Email-existence leak prevention means the
// /request response is byte-identical regardless of whether the email
// matches a tenant — the schemas below capture only the SHAPE of what
// the wire carries; the storage layer is what enforces the equal-time
// equal-shape contract.

// Owner email anchor. Validated client-side as a best-effort hint;
// the DB-level CHECK constraint is the canonical guard. Lower-cased
// + trimmed so case-only differences don't bypass the per-email rate
// limit.
export const OwnerEmailSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(3)
  .max(254)
  .email("must be a valid email address");

// POST /api/v1/merchant/recover/request
export const RecoverRequestBodySchema = z.object({
  email: OwnerEmailSchema,
});
export type RecoverRequestBody = z.infer<typeof RecoverRequestBodySchema>;

// Always returns 200 with this shape regardless of whether the email
// matched. Carries no information about tenant existence (security).
export const RecoverRequestResponseSchema = z.object({
  // Generic copy: "If that email is registered, you'll receive a
  // verification link within a few minutes." — surfaced to the UI.
  message: z.string(),
});
export type RecoverRequestResponse = z.infer<
  typeof RecoverRequestResponseSchema
>;

// POST /api/v1/merchant/recover/verify-link
export const RecoverVerifyLinkBodySchema = z.object({
  requestId: z.string().uuid(),
  // The plaintext link token (32 random bytes, base64url-encoded ~ 43
  // chars). Bcrypt-compared against the stored hash server-side.
  token: z.string().min(20).max(80),
});
export type RecoverVerifyLinkBody = z.infer<
  typeof RecoverVerifyLinkBodySchema
>;

export const RecoverVerifyLinkResponseSchema = z.object({
  requestId: z.string().uuid(),
  expiresAt: z.string().datetime(),
  // Masked phone like "+639XX****XX" — enough for the user to
  // recognize their own number, useless to an attacker.
  ownerPhoneMasked: z.string(),
});
export type RecoverVerifyLinkResponse = z.infer<
  typeof RecoverVerifyLinkResponseSchema
>;

// POST /api/v1/merchant/recover/verify-otp
export const RecoverVerifyOtpBodySchema = z.object({
  requestId: z.string().uuid(),
  // 6-digit numeric, leading zeros preserved (matches generateOtpCode).
  otp: z.string().regex(/^[0-9]{6}$/u, "OTP must be exactly 6 digits"),
});
export type RecoverVerifyOtpBody = z.infer<typeof RecoverVerifyOtpBodySchema>;

export const RecoverVerifyOtpResponseSchema = z.object({
  // The new tenant API token in plaintext. ONLY time it's exposed.
  // The UI must present this to the user with a stark warning that
  // the old token is now invalid and this one cannot be retrieved.
  newApiToken: z.string(),
  // The bcrypt-hashed prefix the new token uses (orbit_live_xxx).
  // Lets the UI render "your new token starts with: orbit_live_…".
  apiTokenPrefix: z.string(),
});
export type RecoverVerifyOtpResponse = z.infer<
  typeof RecoverVerifyOtpResponseSchema
>;

// POST /api/v1/merchant/recover/cancel — graceful UX for users who
// started recovery and changed their mind. Idempotent.
export const RecoverCancelBodySchema = z.object({
  requestId: z.string().uuid(),
});
export type RecoverCancelBody = z.infer<typeof RecoverCancelBodySchema>;

export const RecoverCancelResponseSchema = z.object({
  cancelled: z.boolean(),
});
export type RecoverCancelResponse = z.infer<
  typeof RecoverCancelResponseSchema
>;

// PATCH /api/v1/admin/tenants/:tenantId/owner-phone — admin backfill
// of owner_phone for tenants that pre-date Phase 17.
export const AdminSetTenantOwnerPhoneBodySchema = z.object({
  ownerPhone: PhoneSchema,
});
export type AdminSetTenantOwnerPhoneBody = z.infer<
  typeof AdminSetTenantOwnerPhoneBodySchema
>;

export const AdminSetTenantOwnerPhoneResponseSchema = z.object({
  tenantId: z.string().uuid(),
  ownerPhone: z.string(),
});
export type AdminSetTenantOwnerPhoneResponse = z.infer<
  typeof AdminSetTenantOwnerPhoneResponseSchema
>;

// PATCH /api/v1/admin/tenants/:tenantId — superadmin edits any subset
// of the editable tenant identity fields (name + owner email/phone +
// ops contact phone). Each field uses the same "omitted = leave alone,
// present = overwrite, null = clear" semantics as the merchant
// settings PATCH. `name` is non-empty when present; `ownerEmail` is
// nullable so admins can clear the recovery anchor; `ownerPhone` and
// `contactPhone` are also nullable + reuse PhoneSchema canonicalisation
// when set.
const TenantNameInputSchema = z
  .string()
  .trim()
  .min(2, "Business name must be at least 2 characters.")
  .max(100, "Business name must be 100 characters or less.");

const TenantOwnerEmailInputSchema = z
  .string()
  .trim()
  .toLowerCase()
  .email("Owner email must be a valid email address.")
  .max(160, "Owner email must be 160 characters or less.");

export const AdminUpdateTenantBodySchema = z
  .object({
    name: TenantNameInputSchema.optional(),
    ownerEmail: TenantOwnerEmailInputSchema.nullable().optional(),
    ownerPhone: PhoneSchema.nullable().optional(),
    contactPhone: PhoneSchema.nullable().optional(),
  })
  .refine(
    (v) =>
      v.name !== undefined ||
      v.ownerEmail !== undefined ||
      v.ownerPhone !== undefined ||
      v.contactPhone !== undefined,
    { message: "request must include at least one field to update" },
  );
export type AdminUpdateTenantBody = z.infer<
  typeof AdminUpdateTenantBodySchema
>;

export const AdminUpdateTenantResponseSchema = z.object({
  tenantId: z.string().uuid(),
  name: z.string(),
  ownerEmail: z.string().nullable(),
  ownerPhone: z.string().nullable(),
  contactPhone: z.string().nullable(),
});
export type AdminUpdateTenantResponse = z.infer<
  typeof AdminUpdateTenantResponseSchema
>;

// GET / PATCH /api/v1/tenant/me/profile — merchant-side read + OWNER-
// only write for the same identity fields the admin endpoint above
// touches, MINUS `name` (renaming the tenant has cross-cutting effects
// on the customer-facing brand directory and is admin-only). The
// merchant CAN update their own owner email + phone + ops contact.
export const TenantProfileResponseSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  ownerEmail: z.string().nullable(),
  ownerPhone: z.string().nullable(),
  contactPhone: z.string().nullable(),
});
export type TenantProfileResponse = z.infer<typeof TenantProfileResponseSchema>;

export const TenantProfileUpdateRequestSchema = z
  .object({
    ownerEmail: TenantOwnerEmailInputSchema.nullable().optional(),
    ownerPhone: PhoneSchema.nullable().optional(),
    contactPhone: PhoneSchema.nullable().optional(),
  })
  .refine(
    (v) =>
      v.ownerEmail !== undefined ||
      v.ownerPhone !== undefined ||
      v.contactPhone !== undefined,
    { message: "request must include at least one field to update" },
  );
export type TenantProfileUpdateRequest = z.infer<
  typeof TenantProfileUpdateRequestSchema
>;

// ============================================================================
// Phase 18 — Web Push (VAPID)
// ============================================================================
// Customer-facing push subscription management + dispatch forensics for
// admin Console. The dispatcher itself is server-internal and has no
// wire schema (the payload sent over the push wire is encrypted by
// web-push and rendered by the SW; that shape lives in
// server/push/dispatcher.ts and client/src/sw.ts in matching v1 form).

// GET /api/v1/customer/me/push/vapid-public-key
//
// The browser needs the VAPID public key at `applicationServerKey`-set
// time (when calling registration.pushManager.subscribe). It's not
// secret per the spec, but exposing it via an API call (instead of
// baking it into the client bundle) means a key rotation only requires
// a server restart, not a client redeploy.
export const VapidPublicKeyResponseSchema = z.object({
  publicKey: z.string().min(1),
});
export type VapidPublicKeyResponse = z.infer<typeof VapidPublicKeyResponseSchema>;

// POST /api/v1/customer/me/push/subscriptions
//
// Body shape mirrors what the browser's PushSubscription.toJSON()
// produces: an `endpoint` URL plus base64url-encoded p256dh + auth
// keys nested under `keys`. We accept the wire shape verbatim so the
// client can just `JSON.stringify(subscription)` and POST.
export const PushSubscribeBodySchema = z.object({
  endpoint: z.string().url().min(1).max(2048),
  keys: z.object({
    p256dh: z.string().min(1).max(256),
    auth: z.string().min(1).max(256),
  }),
  // Captured by the client from navigator.userAgent at subscribe time.
  // Optional so a non-browser test client doesn't have to fake one.
  userAgent: z.string().max(512).optional(),
});
export type PushSubscribeBody = z.infer<typeof PushSubscribeBodySchema>;

// Single-subscription DTO surfaced in GET /push/subscriptions and
// returned (for confirmation) by POST /push/subscriptions. We do NOT
// return the auth/p256dh keys — those are server-only secrets that
// should never leave the database.
export const PushSubscriptionResponseSchema = z.object({
  id: z.string().uuid(),
  endpoint: z.string().url(),
  // Best-effort fingerprint shown in the Profile/Notifications "your
  // devices" list so the customer can recognise which device they're
  // looking at. Null when subscribed_at predates platform_hint
  // capture or when the UA wasn't recognised.
  platformHint: z
    .enum([
      "CHROME",
      "FIREFOX",
      "SAFARI_IOS",
      "SAFARI_DESKTOP",
      "EDGE",
      "OTHER",
    ])
    .nullable(),
  userAgent: z.string().nullable(),
  subscribedAt: z.string(),
  lastUsedAt: z.string().nullable(),
});
export type PushSubscriptionResponse = z.infer<
  typeof PushSubscriptionResponseSchema
>;

// GET /api/v1/customer/me/push/subscriptions
export const PushSubscriptionListResponseSchema = z.object({
  subscriptions: z.array(PushSubscriptionResponseSchema),
});
export type PushSubscriptionListResponse = z.infer<
  typeof PushSubscriptionListResponseSchema
>;

// POST /api/v1/customer/me/push/test
//
// Sends a single test push to all the customer's active subscriptions.
// Rate-limited to 3 per hour (server-side) to stop a clicking-finger
// from filling their lock screen.
export const PushTestBodySchema = z.object({
  title: z.string().min(1).max(50).optional(),
  body: z.string().min(1).max(200).optional(),
});
export type PushTestBody = z.infer<typeof PushTestBodySchema>;

export const PushTestResponseSchema = z.object({
  attempted: z.number().int().nonnegative(),
  delivered: z.number().int().nonnegative(),
  errors: z.number().int().nonnegative(),
});
export type PushTestResponse = z.infer<typeof PushTestResponseSchema>;

// GET /api/v1/admin/push/dispatch-log — admin Console forensics.
//
// All filters optional; default sort is attempted_at DESC; pagination
// is offset-based since this view is reached via UI filter rather
// than a high-throughput cursor scroll.
export const AdminDispatchLogQuerySchema = z.object({
  customerId: z.string().uuid().optional(),
  outcomes: z
    .array(
      z.enum([
        "DELIVERED",
        "PUSH_SERVICE_GONE",
        "INVALID_SUBSCRIPTION",
        "NETWORK_ERROR",
        "PAYLOAD_TOO_LARGE",
        "NOT_SUBSCRIBED",
        "OPT_OUT_FILTER",
        "PROVIDER_5XX",
      ]),
    )
    .optional(),
  dateFrom: z.string().datetime().optional(),
  dateTo: z.string().datetime().optional(),
  limit: z.coerce.number().int().min(1).max(200).optional(),
  offset: z.coerce.number().int().min(0).optional(),
});
export type AdminDispatchLogQuery = z.infer<typeof AdminDispatchLogQuerySchema>;

export const AdminDispatchLogRowSchema = z.object({
  id: z.string().uuid(),
  attemptedAt: z.string(),
  customerId: z.string().uuid(),
  customerPhone: z.string().nullable(),
  customerName: z.string().nullable(),
  inboxMessageId: z.string().uuid().nullable(),
  inboxSubject: z.string().nullable(),
  subscriptionId: z.string().uuid().nullable(),
  subscriptionEndpoint: z.string().nullable(),
  outcome: z.enum([
    "DELIVERED",
    "PUSH_SERVICE_GONE",
    "INVALID_SUBSCRIPTION",
    "NETWORK_ERROR",
    "PAYLOAD_TOO_LARGE",
    "NOT_SUBSCRIBED",
    "OPT_OUT_FILTER",
    "PROVIDER_5XX",
  ]),
  responseStatusCode: z.number().int().nullable(),
  responseBody: z.string().nullable(),
});
export type AdminDispatchLogRow = z.infer<typeof AdminDispatchLogRowSchema>;

export const AdminDispatchLogResponseSchema = z.object({
  rows: z.array(AdminDispatchLogRowSchema),
  total: z.number().int().nonnegative(),
  limit: z.number().int().positive(),
  offset: z.number().int().nonnegative(),
});
export type AdminDispatchLogResponse = z.infer<
  typeof AdminDispatchLogResponseSchema
>;

// ============================================================================
// Phase 19 — Surprise Card (daily habit mechanic)
// ============================================================================

export const SurpriseEntryKindSchema = z.enum([
  "POINTS_BONUS",
  "STAMPS_BONUS",
  "VOUCHER_REWARD",
  "NO_PRIZE",
]);
export type SurpriseEntryKind = z.infer<typeof SurpriseEntryKindSchema>;

export const SurpriseClaimOutcomeSchema = z.enum([
  "POINTS_CREDITED",
  "STAMPS_CREDITED",
  "VOUCHER_ISSUED",
  "NO_PRIZE",
]);
export type SurpriseClaimOutcome = z.infer<typeof SurpriseClaimOutcomeSchema>;

// Discriminated payload — matches the storage-phase19 type union.
// Server re-validates shape inside `_assertEntryPayloadShape` as
// defence-in-depth (so a malformed direct-DB row can't crash claim).
export const SurpriseEntryPayloadSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("POINTS_BONUS"),
    points: z.number().int().min(1).max(100000),
  }),
  z.object({
    kind: z.literal("STAMPS_BONUS"),
    stamps: z.number().int().min(1).max(50),
  }),
  z.object({
    kind: z.literal("VOUCHER_REWARD"),
    rewardId: z.string().uuid(),
  }),
  z.object({
    kind: z.literal("NO_PRIZE"),
    message: z.string().max(160).optional(),
  }),
]);
export type SurpriseEntryPayload = z.infer<typeof SurpriseEntryPayloadSchema>;

export const SurpriseCardOutcomeSchema = z.object({
  kind: SurpriseClaimOutcomeSchema,
  pointsCredited: z.number().multipleOf(0.01).nonnegative().optional(),
  stampsCredited: z.number().int().nonnegative().optional(),
  voucherCode: z.string().optional(),
  voucherExpiresAt: z.string().optional(),
  rewardName: z.string().optional(),
  message: z.string().optional(),
});

export const SurpriseCardSchema = z.object({
  tenantId: z.string().uuid(),
  tenantName: z.string(),
  tenantBrandColorPrimary: z.string().nullable(),
  claimDate: z.string(),
  state: z.enum(["AVAILABLE", "CLAIMED", "SKIPPED", "EXPIRED"]),
  entryKind: SurpriseEntryKindSchema,
  displayLabel: z.string(),
  outcome: SurpriseCardOutcomeSchema.nullable(),
});
export type SurpriseCardWire = z.infer<typeof SurpriseCardSchema>;

export const SurpriseCardListResponseSchema = z.object({
  cards: z.array(SurpriseCardSchema),
});
export type SurpriseCardListResponse = z.infer<typeof SurpriseCardListResponseSchema>;

export const SurpriseCardResponseSchema = z.object({
  card: SurpriseCardSchema,
});
export type SurpriseCardResponse = z.infer<typeof SurpriseCardResponseSchema>;

// --- Tenant pool ---

export const SurprisePoolSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  name: z.string(),
  enabled: z.boolean(),
  surprisePushEnabled: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
  enabledEntryCount: z.number().int().nonnegative(),
});
export type SurprisePoolWire = z.infer<typeof SurprisePoolSchema>;

export const SurprisePoolResponseSchema = z.object({ pool: SurprisePoolSchema });
export type SurprisePoolResponse = z.infer<typeof SurprisePoolResponseSchema>;

export const SurprisePoolPatchBodySchema = z
  .object({
    name: z.string().min(1).max(80).optional(),
    enabled: z.boolean().optional(),
    surprisePushEnabled: z.boolean().optional(),
  })
  .refine((o) => Object.keys(o).length > 0, {
    message: "at least one field must be provided",
  });
export type SurprisePoolPatchBody = z.infer<typeof SurprisePoolPatchBodySchema>;

// --- Tenant pool entries ---

export const SurprisePoolEntrySchema = z.object({
  id: z.string().uuid(),
  poolId: z.string().uuid(),
  entryKind: SurpriseEntryKindSchema,
  entryPayload: SurpriseEntryPayloadSchema,
  weight: z.number().int().min(1).max(100),
  enabled: z.boolean(),
  displayLabel: z.string(),
  createdAt: z.string(),
});
export type SurprisePoolEntryWire = z.infer<typeof SurprisePoolEntrySchema>;

export const SurprisePoolEntryListResponseSchema = z.object({
  entries: z.array(SurprisePoolEntrySchema),
});
export type SurprisePoolEntryListResponse = z.infer<
  typeof SurprisePoolEntryListResponseSchema
>;

export const SurprisePoolEntryResponseSchema = z.object({
  entry: SurprisePoolEntrySchema,
});
export type SurprisePoolEntryResponse = z.infer<
  typeof SurprisePoolEntryResponseSchema
>;

export const SurprisePoolEntryCreateBodySchema = z
  .object({
    entryKind: SurpriseEntryKindSchema,
    entryPayload: SurpriseEntryPayloadSchema,
    weight: z.number().int().min(1).max(100),
    enabled: z.boolean().optional(),
    displayLabel: z.string().min(1).max(80),
  })
  .refine((o) => o.entryKind === o.entryPayload.kind, {
    message: "entryKind must match entryPayload.kind",
    path: ["entryPayload"],
  });
export type SurprisePoolEntryCreateBody = z.infer<
  typeof SurprisePoolEntryCreateBodySchema
>;

export const SurprisePoolEntryPatchBodySchema = z
  .object({
    entryPayload: SurpriseEntryPayloadSchema.optional(),
    weight: z.number().int().min(1).max(100).optional(),
    enabled: z.boolean().optional(),
    displayLabel: z.string().min(1).max(80).optional(),
  })
  .refine((o) => Object.keys(o).length > 0, {
    message: "at least one field must be provided",
  });
export type SurprisePoolEntryPatchBody = z.infer<
  typeof SurprisePoolEntryPatchBodySchema
>;

// --- Preview + analytics ---

export const SurprisePreviewQuerySchema = z.object({
  sampleCustomerId: z.string().uuid(),
});
export type SurprisePreviewQuery = z.infer<typeof SurprisePreviewQuerySchema>;

export const SurprisePreviewResponseSchema = z.object({
  date: z.string(),
  selected: SurprisePoolEntrySchema.nullable(),
  totalEnabledWeight: z.number().int().nonnegative(),
  rolledValue: z.number().int().nonnegative(),
});
export type SurprisePreviewResponse = z.infer<
  typeof SurprisePreviewResponseSchema
>;

const DateOnlySchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/u, "must be YYYY-MM-DD");

export const SurpriseAnalyticsQuerySchema = z
  .object({
    fromDate: DateOnlySchema,
    toDate: DateOnlySchema,
  })
  .refine((o) => o.fromDate <= o.toDate, {
    message: "fromDate must be ≤ toDate",
    path: ["toDate"],
  });
export type SurpriseAnalyticsQuery = z.infer<typeof SurpriseAnalyticsQuerySchema>;

export const SurpriseAnalyticsResponseSchema = z.object({
  fromDate: z.string(),
  toDate: z.string(),
  totalSelections: z.number().int().nonnegative(),
  totalClaimed: z.number().int().nonnegative(),
  totalSkipped: z.number().int().nonnegative(),
  totalUnresolved: z.number().int().nonnegative(),
  byOutcome: z.object({
    POINTS_CREDITED: z.number().int().nonnegative(),
    STAMPS_CREDITED: z.number().int().nonnegative(),
    VOUCHER_ISSUED: z.number().int().nonnegative(),
    NO_PRIZE: z.number().int().nonnegative(),
  }),
  byEntry: z.array(
    z.object({
      entryId: z.string().uuid(),
      displayLabel: z.string(),
      selections: z.number().int().nonnegative(),
      claimed: z.number().int().nonnegative(),
    }),
  ),
});
export type SurpriseAnalyticsResponse = z.infer<
  typeof SurpriseAnalyticsResponseSchema
>;

// ────────────────────────────────────────────────────────────────────
// Phase 20 — Streaks & Milestones
// ────────────────────────────────────────────────────────────────────

export const StreakCadenceSchema = z.enum([
  "DAILY",
  "WEEKLY",
  "BIWEEKLY",
  "MONTHLY",
]);
export type StreakCadence = z.infer<typeof StreakCadenceSchema>;

export const MilestoneKindSchema = z.enum([
  "VISIT_COUNT",
  "TENURE_DAYS",
  "SPEND_CENTAVOS",
]);
export type MilestoneKind = z.infer<typeof MilestoneKindSchema>;

// --- Tenant streak config ---

export const StreakConfigResponseSchema = z.object({
  tenantId: z.string().uuid(),
  streakCadence: StreakCadenceSchema,
  streakEnabled: z.boolean(),
  visitMilestonesEnabled: z.boolean(),
  tenureMilestonesEnabled: z.boolean(),
  spendMilestonesEnabled: z.boolean(),
  updatedAt: z.string(),
});
export type StreakConfigResponse = z.infer<typeof StreakConfigResponseSchema>;

export const StreakConfigPatchBodySchema = z
  .object({
    streakCadence: StreakCadenceSchema.optional(),
    streakEnabled: z.boolean().optional(),
    visitMilestonesEnabled: z.boolean().optional(),
    tenureMilestonesEnabled: z.boolean().optional(),
    spendMilestonesEnabled: z.boolean().optional(),
  })
  .refine((o) => Object.keys(o).length > 0, {
    message: "at least one field is required",
  });
export type StreakConfigPatchBody = z.infer<typeof StreakConfigPatchBodySchema>;

// --- Customer streak/milestone reads ---

export const CustomerStreakSchema = z.object({
  tenantId: z.string().uuid(),
  tenantName: z.string(),
  currentStreakCount: z.number().int().nonnegative(),
  longestStreakCount: z.number().int().nonnegative(),
  cadence: StreakCadenceSchema,
  streakStartedAt: z.string().nullable(),
  lastVisitPeriodStart: z.string().nullable(),
  atRisk: z.boolean(),
  periodEndsAt: z.string(),
});
export type CustomerStreak = z.infer<typeof CustomerStreakSchema>;

export const StreakStateListResponseSchema = z.object({
  streaks: z.array(CustomerStreakSchema),
});
export type StreakStateListResponse = z.infer<
  typeof StreakStateListResponseSchema
>;

export const CustomerMilestoneSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  tenantName: z.string(),
  milestoneKind: MilestoneKindSchema,
  threshold: z.number().int().nonnegative(),
  crossedAt: z.string(),
  displayLabel: z.string(),
});
export type CustomerMilestone = z.infer<typeof CustomerMilestoneSchema>;

export const StreakMilestoneListResponseSchema = z.object({
  milestones: z.array(CustomerMilestoneSchema),
  nextCursor: z.string().nullable(),
});
export type StreakMilestoneListResponse = z.infer<
  typeof StreakMilestoneListResponseSchema
>;

// --- Tenant leaderboard + analytics ---

export const StreakLeaderboardEntrySchema = z.object({
  customerId: z.string().uuid(),
  customerDisplayName: z.string(),
  customerPhoneLast4: z.string(),
  currentStreakCount: z.number().int().nonnegative(),
  longestStreakCount: z.number().int().nonnegative(),
  lastVisitAt: z.string().nullable(),
});
export type StreakLeaderboardEntry = z.infer<
  typeof StreakLeaderboardEntrySchema
>;

export const StreakLeaderboardResponseSchema = z.object({
  entries: z.array(StreakLeaderboardEntrySchema),
});
export type StreakLeaderboardResponse = z.infer<
  typeof StreakLeaderboardResponseSchema
>;

export const StreakAnalyticsQuerySchema = z.object({
  from: z.string().datetime({ offset: true }).optional(),
  to: z.string().datetime({ offset: true }).optional(),
});
export type StreakAnalyticsQuery = z.infer<typeof StreakAnalyticsQuerySchema>;

export const StreakAnalyticsResponseSchema = z.object({
  fromIso: z.string(),
  toIso: z.string(),
  totalMilestonesFired: z.number().int().nonnegative(),
  milestonesByKind: z.object({
    VISIT_COUNT: z.number().int().nonnegative(),
    TENURE_DAYS: z.number().int().nonnegative(),
    SPEND_CENTAVOS: z.number().int().nonnegative(),
  }),
  customersWithActiveStreaks: z.number().int().nonnegative(),
  averageCurrentStreakLength: z.number().nonnegative(),
});
export type StreakAnalyticsResponse = z.infer<
  typeof StreakAnalyticsResponseSchema
>;

// =============================================================================
// Phase 21 — Monthly Recap + Year-in-Review
// =============================================================================
//
// Wire schemas for the customer Recap pages (`/api/v1/customer/me/recaps*`)
// and the admin Year-in-Review console (`/api/v1/admin/year-in-review/*`).
// The RecapPayload schema mirrors the RecapPayload TypeScript type in
// lib/db/src/schema.ts — both must change together. JSONB column on the
// db side, validated z.object on the wire side; storage-phase21 reads from
// db.payload (typed) and the renderer + client both consume the wire shape.

const RecapMilestoneKindSchema = z.enum([
  "VISIT_COUNT",
  "TENURE_DAYS",
  "SPEND_CENTAVOS",
]);

const RecapStreakCadenceSchema = z.enum([
  "DAILY",
  "WEEKLY",
  "BIWEEKLY",
  "MONTHLY",
]);

export const RecapKindSchema = z.enum(["MONTHLY", "ANNUAL"]);
export type RecapKind = z.infer<typeof RecapKindSchema>;

export const RecapPayloadSchema = z.object({
  periodStart: z.string(),
  periodEnd: z.string(),
  kind: RecapKindSchema,
  totals: z.object({
    visits: z.number().int().nonnegative(),
    pointsEarned: z.number().multipleOf(0.01).nonnegative(),
    stampsEarned: z.number().int().nonnegative(),
    vouchersRedeemed: z.number().int().nonnegative(),
    estimatedSavingsCentavos: z.number().int().nonnegative(),
    activeMerchants: z.number().int().nonnegative(),
    enrolledMerchants: z.number().int().nonnegative(),
  }),
  topMerchantByVisits: z
    .object({
      tenantId: z.string().uuid(),
      tenantName: z.string(),
      visits: z.number().int().nonnegative(),
    })
    .nullable(),
  topMerchantBySpendCentavos: z
    .object({
      tenantId: z.string().uuid(),
      tenantName: z.string(),
      spendCentavos: z.number().int().nonnegative(),
    })
    .nullable(),
  longestStreak: z
    .object({
      tenantId: z.string().uuid(),
      tenantName: z.string(),
      cadence: RecapStreakCadenceSchema,
      streakCount: z.number().int().nonnegative(),
      isPersonalRecord: z.boolean(),
    })
    .nullable(),
  milestonesHit: z.array(
    z.object({
      tenantId: z.string().uuid(),
      tenantName: z.string(),
      milestoneKind: RecapMilestoneKindSchema,
      threshold: z.number().int().positive(),
      crossedAt: z.string(),
    }),
  ),
  highlight: z
    .union([
      z.object({
        kind: z.literal("MILESTONE"),
        tenantId: z.string().uuid(),
        tenantName: z.string(),
        milestoneKind: RecapMilestoneKindSchema,
        threshold: z.number().int().positive(),
      }),
      z.object({
        kind: z.literal("NEW_MERCHANT"),
        tenantId: z.string().uuid(),
        tenantName: z.string(),
        joinedAt: z.string(),
      }),
      z.object({
        kind: z.literal("STREAK_RECORD"),
        tenantId: z.string().uuid(),
        tenantName: z.string(),
        streakCount: z.number().int().nonnegative(),
        cadence: RecapStreakCadenceSchema,
      }),
      z.object({
        kind: z.literal("BIGGEST_TRANSACTION"),
        tenantId: z.string().uuid(),
        tenantName: z.string(),
        amountCentavos: z.number().int().nonnegative(),
      }),
    ])
    .nullable(),
});
export type RecapPayloadWire = z.infer<typeof RecapPayloadSchema>;

export const CustomerRecapSummarySchema = z.object({
  id: z.string().uuid(),
  kind: RecapKindSchema,
  periodStart: z.string(),
  periodEnd: z.string(),
  generatedAt: z.string(),
});
export type CustomerRecapSummary = z.infer<typeof CustomerRecapSummarySchema>;

export const CustomerRecapListResponseSchema = z.object({
  items: z.array(CustomerRecapSummarySchema),
  nextCursor: z.string().nullable(),
});
export type CustomerRecapListResponse = z.infer<
  typeof CustomerRecapListResponseSchema
>;

export const CustomerRecapDetailResponseSchema = z.object({
  id: z.string().uuid(),
  kind: RecapKindSchema,
  periodStart: z.string(),
  periodEnd: z.string(),
  generatedAt: z.string(),
  customerName: z.string(),
  periodLabel: z.string(),
  payload: RecapPayloadSchema,
  imageUrl: z.string(),
});
export type CustomerRecapDetailResponse = z.infer<
  typeof CustomerRecapDetailResponseSchema
>;

export const InboxPayloadRecapAvailableSchema = z.object({
  recapId: z.string().uuid(),
  kind: RecapKindSchema,
  periodStart: z.string(),
  periodEnd: z.string(),
});
export type InboxPayloadRecapAvailable = z.infer<
  typeof InboxPayloadRecapAvailableSchema
>;

// ----- Admin Year-in-Review dispatch -----

export const AdminYearInReviewDispatchRequestSchema = z.object({
  year: z.number().int().min(2024).max(2100),
  sendInbox: z.boolean().optional().default(true),
  sendSms: z.boolean().optional().default(false),
  dryRun: z.boolean().optional().default(false),
});
export type AdminYearInReviewDispatchRequest = z.infer<
  typeof AdminYearInReviewDispatchRequestSchema
>;

export const AdminYearInReviewDispatchResponseSchema = z.object({
  year: z.number().int(),
  dryRun: z.boolean(),
  candidateCustomers: z.number().int().nonnegative(),
  recapsGenerated: z.number().int().nonnegative(),
  inboxMessagesCreated: z.number().int().nonnegative(),
  smsesSent: z.number().int().nonnegative(),
  smsesSkipped: z.number().int().nonnegative(),
});
export type AdminYearInReviewDispatchResponse = z.infer<
  typeof AdminYearInReviewDispatchResponseSchema
>;

export const AdminYearInReviewRecentRunSchema = z.object({
  periodKey: z.string(),
  recapKind: RecapKindSchema,
  completedAt: z.string(),
  recapsGenerated: z.number().int().nonnegative(),
  inboxMessagesCreated: z.number().int().nonnegative(),
  smsesSent: z.number().int().nonnegative(),
});
export type AdminYearInReviewRecentRun = z.infer<
  typeof AdminYearInReviewRecentRunSchema
>;

export const AdminYearInReviewRecentResponseSchema = z.object({
  runs: z.array(AdminYearInReviewRecentRunSchema),
});
export type AdminYearInReviewRecentResponse = z.infer<
  typeof AdminYearInReviewRecentResponseSchema
>;

// ============================================================
// Phase A (Wave 5) — admin tenant creation + token issuance
// ============================================================
//
// All schemas here are consumed by `routes-admin-phaseA.ts` for
// request validation and by the admin-client UI for type safety.
// VoucherPrefix bounds match the DB CHECK constraint introduced in
// migration 011 (`^[A-Z0-9]{2,6}$`); we duplicate the regex here so
// validation fails at the API boundary with a friendly message
// instead of bubbling a Postgres CHECK violation.

export const VoucherPrefixSchema = z
  .string()
  .trim()
  .regex(
    /^[A-Z0-9]{2,6}$/u,
    "voucher_prefix must be 2-6 uppercase letters or digits.",
  );

// join_short_code accepts mixed case for the same reason ShortCodeSchema
// does (see comment above ShortCodeSchema). Server-side lookup is
// case-insensitive and migration 027 enforces uniqueness on
// lower(join_short_code).
export const JoinShortCodeSchema = z
  .string()
  .trim()
  .min(3)
  .max(64)
  .regex(
    /^[A-Za-z0-9-]+$/u,
    "join_short_code must be letters, digits, or hyphens.",
  );

export const ConnectedViaSchema = z.enum([
  "BARESCA",
  "CARWASH_OS",
  "HAOS",
  "STANDALONE",
]);
export type ConnectedVia = z.infer<typeof ConnectedViaSchema>;

export const TokenIssuanceReasonSchema = z.enum([
  "INITIAL_CREATION",
  "REGENERATION_LEAK_SUSPECTED",
  "REGENERATION_TENANT_REQUESTED",
  "REGENERATION_PERIODIC",
]);
export type TokenIssuanceReason = z.infer<typeof TokenIssuanceReasonSchema>;

// POST /admin/tenants
export const AdminCreateTenantRequestSchema = z.object({
  name: z.string().trim().min(1).max(200),
  joinShortCode: JoinShortCodeSchema,
  voucherPrefix: VoucherPrefixSchema,
  verticalOs: VerticalOsSchema,
  subscriptionTier: SubscriptionTierSchema,
  loyaltyMode: LoyaltyModeSchema,
  ownerEmail: z.string().trim().toLowerCase().email(),
  ownerPhone: PhoneSchema,
  contactPhone: PhoneSchema.nullable().optional(),
});
export type AdminCreateTenantRequest = z.infer<
  typeof AdminCreateTenantRequestSchema
>;

export const AdminCreateTenantResponseSchema = z.object({
  tenantId: z.string().uuid(),
  name: z.string(),
  joinShortCode: z.string(),
  voucherPrefix: z.string(),
  apiTokenPrefix: z.string(),
  // Returned EXACTLY ONCE — the admin must copy this immediately.
  // Subsequent reads (GET /admin/tenants, GET token-history) NEVER
  // return the plaintext.
  plainTextApiToken: z.string(),
  createdAt: z.string(),
});
export type AdminCreateTenantResponse = z.infer<
  typeof AdminCreateTenantResponseSchema
>;

// POST /admin/tenants/:id/regenerate-token
export const AdminRegenerateTenantTokenRequestSchema = z.object({
  reason: z.enum([
    "REGENERATION_LEAK_SUSPECTED",
    "REGENERATION_TENANT_REQUESTED",
    "REGENERATION_PERIODIC",
  ]),
});
export type AdminRegenerateTenantTokenRequest = z.infer<
  typeof AdminRegenerateTenantTokenRequestSchema
>;

export const AdminRegenerateTenantTokenResponseSchema = z.object({
  tenantId: z.string().uuid(),
  apiTokenPrefix: z.string(),
  // Returned EXACTLY ONCE; same caveat as above.
  plainTextApiToken: z.string(),
  rotatedAt: z.string(),
});
export type AdminRegenerateTenantTokenResponse = z.infer<
  typeof AdminRegenerateTenantTokenResponseSchema
>;

// GET /admin/tenants/v2 (cursor pagination)
export const AdminTenantPhaseAListItemSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  joinShortCode: z.string(),
  verticalOs: z.string(),
  status: z.string(),
  subscriptionTier: z.string(),
  loyaltyMode: z.string(),
  voucherPrefix: z.string(),
  ownerEmail: z.string().nullable(),
  ownerPhone: z.string().nullable(),
  contactPhone: z.string().nullable(),
  apiTokenPrefix: z.string(),
  apiTokenRotatedAt: z.string().nullable(),
  connectedVia: z.string().nullable(),
  connectedViaVersion: z.string().nullable(),
  firstExternalCallAt: z.string().nullable(),
  lastExternalCallAt: z.string().nullable(),
  createdVia: z.string(),
  createdByAdminId: z.string().uuid().nullable(),
  createdAt: z.string(),
});
export type AdminTenantPhaseAListItem = z.infer<
  typeof AdminTenantPhaseAListItemSchema
>;

export const AdminTenantPhaseACursorSchema = z.object({
  createdAt: z.string(),
  id: z.string().uuid(),
});
export type AdminTenantPhaseACursor = z.infer<
  typeof AdminTenantPhaseACursorSchema
>;

export const AdminTenantPhaseAListResponseSchema = z.object({
  tenants: z.array(AdminTenantPhaseAListItemSchema),
  nextCursor: AdminTenantPhaseACursorSchema.nullable(),
});
export type AdminTenantPhaseAListResponse = z.infer<
  typeof AdminTenantPhaseAListResponseSchema
>;

// GET /admin/tenants/:id/token-history
export const AdminTenantTokenIssuanceItemSchema = z.object({
  id: z.string().uuid(),
  issuedAt: z.string(),
  reason: z.string(),
  prefixFirstEight: z.string(),
  // Nullable since Phase 28 (D96): self-serve signups have no admin
  // actor. When null, the issuance is identified by a join to
  // tenants.created_via = 'SELF_SERVE_SIGNUP'.
  issuedByAdminId: z.string().uuid().nullable(),
  issuedByAdminEmail: z.string().nullable(),
});
export type AdminTenantTokenIssuanceItem = z.infer<
  typeof AdminTenantTokenIssuanceItemSchema
>;

export const AdminTenantTokenIssuanceListResponseSchema = z.object({
  issuances: z.array(AdminTenantTokenIssuanceItemSchema),
});
export type AdminTenantTokenIssuanceListResponse = z.infer<
  typeof AdminTenantTokenIssuanceListResponseSchema
>;

// ============================================================================
// Phase 26 — Customer plate registration
// ============================================================================
//
// Customer-managed vehicle plate metadata exposed at /customer/me/vehicles.
// Cap of 5 plates per customer is enforced server-side (storage layer);
// the schemas below cover format + length only.

export const VehicleSizeSchema = z.enum([
  "SMALL",
  "MEDIUM",
  "LARGE",
  "EXTRA_LARGE",
]);
export type VehicleSize = z.infer<typeof VehicleSizeSchema>;

/**
 * Canonical plate form on the wire: trimmed, uppercased, internal
 * whitespace collapsed to single spaces. The Zod transform applies
 * this so client form input ("abc 1234", "  abc-1234  ", etc.) all
 * arrive at the storage layer as the same canonical string. The
 * storage layer re-applies the same normalization (defense in depth)
 * and the DB CHECK constraints are the final backstop.
 */
const PlateNumberSchema = z
  .string()
  .min(1, "Plate is required.")
  .transform((s) => s.trim().toUpperCase().replace(/\s+/g, " "))
  .pipe(
    z
      .string()
      .min(2, "Plate must be at least 2 characters.")
      .max(12, "Plate must be at most 12 characters.")
      .regex(
        /^[A-Z0-9 -]+$/,
        "Plate may contain only letters, digits, hyphens, and spaces.",
      ),
  );

// Migration 032 — optional brand/model free-text helper. Trim,
// length-cap, and coerce empty → null so the form's "" cleared
// state lands as NULL in the DB.
const optionalVehicleText = (max: number) =>
  z
    .union([
      z
        .string()
        .trim()
        .max(max)
        .transform((s) => (s.length === 0 ? null : s)),
      z.null(),
    ])
    .optional();

export const CustomerVehicleSchema = z.object({
  id: z.string().uuid(),
  plateNumber: z.string(),
  nickname: z.string().nullable(),
  vehicleSize: VehicleSizeSchema.nullable(),
  // Migration 032 — optional brand + model.
  brand: z.string().nullable(),
  model: z.string().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type CustomerVehicleResponse = z.infer<typeof CustomerVehicleSchema>;

export const CustomerVehicleListResponseSchema = z.object({
  vehicles: z.array(CustomerVehicleSchema),
  count: z.number().int().nonnegative(),
  max: z.number().int().positive(),
});
export type CustomerVehicleListResponse = z.infer<
  typeof CustomerVehicleListResponseSchema
>;

export const AddVehicleRequestSchema = z.object({
  plateNumber: PlateNumberSchema,
  nickname: z
    .string()
    .trim()
    .max(50, "Nickname must be 50 characters or less.")
    .optional()
    .transform((v) => (v && v.length > 0 ? v : null))
    .nullable(),
  vehicleSize: VehicleSizeSchema.optional().nullable(),
  // Migration 032 — optional brand + model.
  brand: optionalVehicleText(40),
  model: optionalVehicleText(60),
});
export type AddVehicleRequest = z.infer<typeof AddVehicleRequestSchema>;

/**
 * PATCH body — plate number is intentionally NOT mutable. To change
 * a plate the customer deletes and re-adds (prevents accidental
 * rename that would orphan any future references).
 */
export const UpdateVehicleRequestSchema = z
  .object({
    nickname: z
      .string()
      .trim()
      .max(50, "Nickname must be 50 characters or less.")
      .nullable()
      .optional(),
    vehicleSize: VehicleSizeSchema.nullable().optional(),
    // Migration 032 — optional brand + model.
    brand: optionalVehicleText(40),
    model: optionalVehicleText(60),
  })
  .strict();
export type UpdateVehicleRequest = z.infer<typeof UpdateVehicleRequestSchema>;

export const AddVehicleResponseSchema = z.object({
  vehicle: CustomerVehicleSchema,
});
export type AddVehicleResponse = z.infer<typeof AddVehicleResponseSchema>;

export const UpdateVehicleResponseSchema = z.object({
  vehicle: CustomerVehicleSchema,
});
export type UpdateVehicleResponse = z.infer<typeof UpdateVehicleResponseSchema>;

export const DeleteVehicleResponseSchema = z.object({
  deleted: z.literal(true),
  vehicleId: z.string().uuid(),
});
export type DeleteVehicleResponse = z.infer<typeof DeleteVehicleResponseSchema>;

/**
 * Phase 26 export extension (D92). Appended to CustomerDataExportSchema
 * via .extend below so the existing schema stays the canonical type and
 * old call sites (Phase 12 spec, ProfileDataExport.tsx download flow)
 * keep working with no modification.
 */
export const CustomerVehicleExportItemSchema = z.object({
  plateNumber: z.string(),
  nickname: z.string().nullable(),
  vehicleSize: VehicleSizeSchema.nullable(),
  // Migration 032 — included in export so customers see the full
  // record on data download.
  brand: z.string().nullable(),
  model: z.string().nullable(),
  addedAt: z.string().datetime(),
});
export type CustomerVehicleExportItem = z.infer<
  typeof CustomerVehicleExportItemSchema
>;

// =============================================================================
// Phase 28 — standalone tenant self-serve onboarding
// =============================================================================
//
// Public-facing merchant signup state machine. A merchant submits the
// signup form, verifies their phone via OTP, then completes — getting
// back a free STANDALONE tier tenant + plaintext API token. The
// schemas mirror that linear flow:
//
//   POST /public/merchant-signup/initiate   ⇒ Initiate {Request,Response}
//   POST /public/merchant-signup/verify-otp ⇒ VerifyOtp {Request,Response}
//   POST /public/merchant-signup/complete   ⇒ Complete  {Request,Response}
//   POST /public/merchant-signup/cancel     ⇒ Cancel    {Request,Response}
//
// Phone is canonicalized via the existing `canonicalizePhone` helper
// (line ~646 above) at the route layer — the Zod schema only checks
// the raw input is a non-empty string so the route can return a
// crisp INVALID_PHONE error code rather than a verbose Zod message.
// Voucher prefix is uppercased and length-checked here so the
// downstream storage code can trust the shape.

export const MerchantSignupInitiateRequestSchema = z
  .object({
    businessName: z
      .string()
      .trim()
      .min(2, "Business name must be at least 2 characters.")
      .max(100, "Business name must be 100 characters or less."),
    ownerName: z
      .string()
      .trim()
      .min(2, "Owner name must be at least 2 characters.")
      .max(80, "Owner name must be 80 characters or less."),
    // Loose validation here — `canonicalizePhone()` at the route
    // layer is the single source of truth for the +639XXXXXXXXX
    // canonical form. Returning INVALID_PHONE there gives a cleaner
    // error than a Zod regex failure embedded inside a payload.
    ownerPhone: z
      .string()
      .trim()
      .min(7, "Phone number is too short.")
      .max(20, "Phone number is too long."),
    ownerEmail: z
      .string()
      .trim()
      .toLowerCase()
      .email("Owner email must be a valid email address.")
      .max(160, "Owner email must be 160 characters or less."),
    voucherPrefix: z
      .string()
      .trim()
      .transform((s) => s.toUpperCase())
      // After the .transform the regex sees uppercase only, but
      // we keep [A-Z0-9] explicit (rather than .regex(/^[A-Z0-9]+$/i))
      // so a future schema reader doesn't think mixed-case is OK.
      .pipe(
        z
          .string()
          .regex(
            /^[A-Z0-9]{2,6}$/u,
            "Voucher prefix must be 2–6 uppercase letters or digits.",
          ),
      ),
  })
  .strict();
export type MerchantSignupInitiateRequest = z.infer<
  typeof MerchantSignupInitiateRequestSchema
>;

export const MerchantSignupInitiateResponseSchema = z.object({
  attemptToken: z.string(),
  // ISO timestamp; the client surfaces the remaining minutes in the
  // verify page so the merchant knows how long they have to enter
  // the OTP before having to start over.
  expiresAt: z.string().datetime(),
  // Masked form of the canonicalized phone (e.g. "+63917***4567").
  // Lets the verify page render "We sent a code to …" without
  // round-tripping the original phone through the browser's session
  // storage in plaintext.
  ownerPhoneMasked: z.string(),
});
export type MerchantSignupInitiateResponse = z.infer<
  typeof MerchantSignupInitiateResponseSchema
>;

export const MerchantSignupVerifyOtpRequestSchema = z
  .object({
    attemptToken: z.string().min(20, "Invalid attempt token."),
    otp: z
      .string()
      .trim()
      .regex(/^\d{6}$/u, "OTP must be a 6-digit code."),
  })
  .strict();
export type MerchantSignupVerifyOtpRequest = z.infer<
  typeof MerchantSignupVerifyOtpRequestSchema
>;

export const MerchantSignupVerifyOtpResponseSchema = z.object({
  attemptToken: z.string(),
  // Pass through so the client can surface "OTP verified — tap
  // Continue to create your tenant" without an extra GET round-trip.
  step: z.literal("OTP_VERIFIED"),
});
export type MerchantSignupVerifyOtpResponse = z.infer<
  typeof MerchantSignupVerifyOtpResponseSchema
>;

export const MerchantSignupCompleteRequestSchema = z
  .object({
    attemptToken: z.string().min(20, "Invalid attempt token."),
  })
  .strict();
export type MerchantSignupCompleteRequest = z.infer<
  typeof MerchantSignupCompleteRequestSchema
>;

export const MerchantSignupCompleteResponseSchema = z.object({
  tenantId: z.string().uuid(),
  name: z.string(),
  joinShortCode: z.string(),
  voucherPrefix: z.string(),
  apiTokenPrefix: z.string(),
  // Plaintext API token — returned to the merchant exactly once.
  // The bcrypt hash is the only persisted form on the tenants row,
  // so a refresh of the complete page returns 410 ALREADY_COMPLETED
  // and the merchant must use the cashier "lost token" recovery
  // flow if they didn't save it. Same one-shot rule the admin path
  // (POST /admin/tenants) follows.
  plainTextApiToken: z.string(),
  // Drives the client's auto-login step: the page stashes
  // autoLogin.token via setMerchantToken() and navigates to
  // autoLogin.dashboardUrl. Server-driven so future routing
  // changes don't require a coordinated client release.
  autoLogin: z.object({
    token: z.string(),
    dashboardUrl: z.string(),
  }),
});
export type MerchantSignupCompleteResponse = z.infer<
  typeof MerchantSignupCompleteResponseSchema
>;

export const MerchantSignupCancelRequestSchema = z
  .object({
    attemptToken: z.string().min(20, "Invalid attempt token."),
  })
  .strict();
export type MerchantSignupCancelRequest = z.infer<
  typeof MerchantSignupCancelRequestSchema
>;

export const MerchantSignupCancelResponseSchema = z.object({
  cancelled: z.literal(true),
});
export type MerchantSignupCancelResponse = z.infer<
  typeof MerchantSignupCancelResponseSchema
>;

// ============================================================================
// Phase 31 — Forgot-password reset (public, 3-step) + set-password (authed)
// ============================================================================
// Decision D103. Closes the recovery gap left by passwordless QR-join
// (signup-via-join mints customer rows with password_hash NULL).
//
// Public 3-step flow:
//   1. POST /auth/password-reset/request    { phone } → { resetToken, ... }
//   2. POST /auth/password-reset/verify-otp { resetToken, code } → { otpVerified }
//   3. POST /auth/password-reset/complete   { resetToken, newPassword } → { tokens }
//
// Authenticated set-or-change:
//   POST /customer/me/password { currentPassword?, newPassword } → { passwordSet }
//
// Privacy stance for /request: returns a uniform 200 response shape
// even when the phone is unregistered, the customer has no SMS sender
// configured, or rate-limit cap is hit. This avoids enumeration of
// customer phones via the reset endpoint (login already does the same
// "constant-time bcrypt" trick). The resetToken returned in those
// cases is a fake-looking opaque string that won't validate at /verify.

// Phase 32 (D104) — discriminated union by channel. The phone shape is
// kept identical to Phase 31 for back-compat: a legacy payload of
// `{ phone }` with no `channel` field is preprocessed to
// `{ channel: 'phone', phone }` before discrimination, so older PWA
// bundles in flight (and any external integration written against the
// Phase 31 contract) keep working. Email shape carries the raw email
// string; the server canonicalizes to lowercase via EmailSchema before
// lookup.
export const PasswordResetRequestSchema = z.preprocess((raw) => {
  if (
    raw &&
    typeof raw === "object" &&
    !Array.isArray(raw) &&
    !("channel" in (raw as Record<string, unknown>)) &&
    "phone" in (raw as Record<string, unknown>)
  ) {
    return { channel: "phone", ...(raw as Record<string, unknown>) };
  }
  return raw;
}, z.discriminatedUnion("channel", [
  z.object({
    channel: z.literal("phone"),
    phone: z.string().min(7).max(20),
  }),
  z.object({
    channel: z.literal("email"),
    email: EmailSchema,
  }),
]));
export type PasswordResetRequest = z.infer<typeof PasswordResetRequestSchema>;

export const PasswordResetRequestResponseSchema = z.object({
  resetToken: z.string(),
  // ISO timestamp; the attempt row's expires_at. The client uses it
  // to render the "Resend in N:NN" countdown without an extra round-trip.
  expiresAt: z.string(),
  // Phase 32 (D104) — echoes the channel back so the verify page knows
  // whether to render "We texted ****567" or "We emailed m***a@x.com".
  channel: z.enum(["phone", "email"]),
  // Channel-appropriate masked target (phone OR email). Always returned
  // — the mask is derived from the input even when no customer is
  // found, preserving uniform-response privacy.
  targetMasked: z.string(),
  // DEPRECATED — kept for back-compat with pre-Phase-32 clients that
  // expect `phoneMasked`. New clients should read `targetMasked` and
  // branch on `channel`. Always populated for phone channel; for
  // email channel, contains the targetMasked email so older clients
  // at least show something coherent.
  phoneMasked: z.string(),
});
export type PasswordResetRequestResponse = z.infer<
  typeof PasswordResetRequestResponseSchema
>;

export const PasswordResetVerifyOtpRequestSchema = z.object({
  resetToken: z.string().min(1).max(200),
  code: z.string().regex(/^\d{6}$/, "Enter the 6-digit code."),
});
export type PasswordResetVerifyOtpRequest = z.infer<
  typeof PasswordResetVerifyOtpRequestSchema
>;

export const PasswordResetVerifyOtpResponseSchema = z.object({
  resetToken: z.string(),
  otpVerified: z.literal(true),
});
export type PasswordResetVerifyOtpResponse = z.infer<
  typeof PasswordResetVerifyOtpResponseSchema
>;

export const PasswordResetCompleteRequestSchema = z.object({
  resetToken: z.string().min(1).max(200),
  newPassword: CustomerPasswordSchema,
});
export type PasswordResetCompleteRequest = z.infer<
  typeof PasswordResetCompleteRequestSchema
>;

export const PasswordResetCompleteResponseSchema = z.object({
  customerId: z.string().uuid(),
  tokens: AuthTokensSchema,
});
export type PasswordResetCompleteResponse = z.infer<
  typeof PasswordResetCompleteResponseSchema
>;

// ----- POST /customer/me/password (customerAuth) -----
// `currentPassword` is REQUIRED when the customer already has a
// password set, OPTIONAL when password_hash IS NULL (the post-join
// "set initial password" case). The server enforces this at the
// boundary; the schema allows omitting it so the SetPassword page
// can submit just { newPassword }.
export const SetCustomerPasswordRequestSchema = z.object({
  currentPassword: z.string().min(1).max(200).optional(),
  newPassword: CustomerPasswordSchema,
  // Phase 32 (D104) — when provided, the server stores it on the
  // customer row IF and ONLY IF the customer has no email yet.
  // Lets the post-QR-join SetPassword page capture both credentials
  // in one round-trip without a separate /customer/me/email call.
  // Storing here never sets emailVerified — the badge is unlocked
  // via the existing /auth/verify-email/request flow.
  email: EmailSchema.optional(),
});
export type SetCustomerPasswordRequest = z.infer<
  typeof SetCustomerPasswordRequestSchema
>;

export const SetCustomerPasswordResponseSchema = z.object({
  passwordSet: z.literal(true),
});
export type SetCustomerPasswordResponse = z.infer<
  typeof SetCustomerPasswordResponseSchema
>;

// ============================================================================
// Phase 34 (D107) — tenant subscription tier menu + change requests.
// ============================================================================

export const TenantPlanChangeRequestSchema = z.object({
  id: z.string().uuid(),
  currentTier: SubscriptionTierSchema,
  requestedTier: SubscriptionTierSchema,
  billingCycle: z.enum(["MONTHLY", "YEARLY"]),
  note: z.string().nullable(),
  status: z.enum(["PENDING", "APPROVED", "REJECTED", "CANCELLED"]),
  decisionReason: z.string().nullable(),
  createdAt: z.string(),
  decidedAt: z.string().nullable(),
});
export type TenantPlanChangeRequestDto = z.infer<
  typeof TenantPlanChangeRequestSchema
>;

export const TierCatalogEntryDtoSchema = z.object({
  tier: SubscriptionTierSchema,
  name: z.string(),
  tagline: z.string(),
  monthlyPricePhp: z.string(),
  yearlyPricePhp: z.string(),
  highlights: z.array(z.string()),
});
export const TierFeatureRowDtoSchema = z.object({
  label: z.string(),
  values: z.record(SubscriptionTierSchema, z.string()),
});

export const TierUsageSnapshotSchema = z.object({
  enrolledCustomers: z.number().int().nonnegative(),
});
export type TierUsageSnapshot = z.infer<typeof TierUsageSnapshotSchema>;

export const TierOverQuotaItemSchema = z.object({
  metric: z.enum([
    "enrolledCustomers",
    "monthlyTransactions",
    "monthlyPushNotifications",
  ]),
  label: z.string(),
  current: z.number().int().nonnegative(),
  limit: z.number().int().nonnegative(),
});
export type TierOverQuotaItem = z.infer<typeof TierOverQuotaItemSchema>;

export const TenantPlansResponseSchema = z.object({
  currentTier: SubscriptionTierSchema,
  pendingRequest: TenantPlanChangeRequestSchema.nullable(),
  // Phase 34 — server-rendered catalog so non-PWA clients (curl,
  // future Vertical OS dashboards) don't need to import @orbit/api-zod
  // to render the tier menu. The PWA still imports the catalog
  // statically for SSR-free first paint, but consumes the same shape
  // here as a sanity check.
  catalog: z.array(TierCatalogEntryDtoSchema),
  featureMatrix: z.array(TierFeatureRowDtoSchema),
  // Live usage vs. the *current* tier's quotas. `overQuota` is
  // populated whenever a metric exceeds its cap — typically the
  // result of an admin-approved downgrade landing on a tenant
  // whose usage hasn't shrunk. The merchant UI surfaces an inline
  // banner per item; the list is empty (and the banner hidden)
  // once usage is back under the cap.
  usage: TierUsageSnapshotSchema,
  overQuota: z.array(TierOverQuotaItemSchema),
});
export type TenantPlansResponse = z.infer<typeof TenantPlansResponseSchema>;

export const RequestPlanChangeRequestSchema = z
  .object({
    requestedTier: SubscriptionTierSchema,
    billingCycle: z.enum(["MONTHLY", "YEARLY"]),
    note: z.string().trim().max(1000).optional(),
  });
export type RequestPlanChangeRequest = z.infer<
  typeof RequestPlanChangeRequestSchema
>;

export const RequestPlanChangeResponseSchema = z.object({
  request: TenantPlanChangeRequestSchema,
});
export type RequestPlanChangeResponse = z.infer<
  typeof RequestPlanChangeResponseSchema
>;

// ----- Admin queue --------------------------------------------------

export const AdminPlanChangeRequestRowSchema = TenantPlanChangeRequestSchema
  .extend({
    tenantId: z.string().uuid(),
    tenantName: z.string(),
    tenantShortCode: z.string(),
    approvedByAdminEmail: z.string().nullable(),
  });
export type AdminPlanChangeRequestRow = z.infer<
  typeof AdminPlanChangeRequestRowSchema
>;

export const AdminPlanChangesListResponseSchema = z.object({
  requests: z.array(AdminPlanChangeRequestRowSchema),
  total: z.number().int().nonnegative(),
});
export type AdminPlanChangesListResponse = z.infer<
  typeof AdminPlanChangesListResponseSchema
>;

export const AdminPlanChangeApproveRequestSchema = z.object({
  note: z.string().trim().max(1000).optional(),
});
export type AdminPlanChangeApproveRequest = z.infer<
  typeof AdminPlanChangeApproveRequestSchema
>;

export const AdminPlanChangeRejectRequestSchema = z.object({
  reason: z.string().trim().min(1).max(1000),
});
export type AdminPlanChangeRejectRequest = z.infer<
  typeof AdminPlanChangeRejectRequestSchema
>;

// ============================================================================
// Phase 38 — Tenant SMS credits.
// ============================================================================

export const TenantSmsCreditEntryTypeSchema = z.enum([
  "TOP_UP",
  "DEBIT",
  "REFUND",
]);
export type TenantSmsCreditEntryType = z.infer<
  typeof TenantSmsCreditEntryTypeSchema
>;

export const TenantSmsCreditsSummarySchema = z.object({
  tenantId: z.string().uuid(),
  balance: z.number().int().nonnegative(),
  lifetimePurchased: z.number().int().nonnegative(),
  lifetimeConsumed: z.number().int().nonnegative(),
  updatedAt: z.string().datetime(),
});
export type TenantSmsCreditsSummary = z.infer<
  typeof TenantSmsCreditsSummarySchema
>;

export const TenantSmsCreditLedgerEntrySchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  entryType: TenantSmsCreditEntryTypeSchema,
  delta: z.number().int(),
  balanceAfter: z.number().int().nonnegative(),
  actorAdminId: z.string().uuid().nullable(),
  actorAdminEmail: z.string().nullable(),
  reason: z.string(),
  relatedDispatchId: z.string().uuid().nullable(),
  createdAt: z.string().datetime(),
});
export type TenantSmsCreditLedgerEntry = z.infer<
  typeof TenantSmsCreditLedgerEntrySchema
>;

export const AdminSmsCreditsTopUpRequestSchema = z.object({
  amount: z.number().int().min(1).max(100000),
  reason: z.string().trim().min(1).max(500),
});
export type AdminSmsCreditsTopUpRequest = z.infer<
  typeof AdminSmsCreditsTopUpRequestSchema
>;

export const AdminSmsCreditsLedgerResponseSchema = z.object({
  summary: TenantSmsCreditsSummarySchema,
  entries: z.array(TenantSmsCreditLedgerEntrySchema),
});
export type AdminSmsCreditsLedgerResponse = z.infer<
  typeof AdminSmsCreditsLedgerResponseSchema
>;

// ============================================================================
// Phase 40 — Tenant users (Owner / Manager / Staff RBAC).
// ============================================================================

export const TenantUserRoleSchema = z.enum(["OWNER", "MANAGER", "STAFF"]);
export type TenantUserRole = z.infer<typeof TenantUserRoleSchema>;

export const TenantUserStatusSchema = z.enum(["ACTIVE", "SUSPENDED"]);
export type TenantUserStatus = z.infer<typeof TenantUserStatusSchema>;

const tenantUserPasswordSchema = z
  .string()
  .min(10, "Password must be at least 10 characters.")
  .max(200);

// ----- Auth: login -----
export const TenantAuthLoginRequestSchema = z.object({
  email: z.string().email().max(254),
  password: z.string().min(1).max(200),
  // Optional disambiguator when the same email exists on multiple tenants.
  tenantId: z.string().uuid().optional(),
});
export type TenantAuthLoginRequest = z.infer<
  typeof TenantAuthLoginRequestSchema
>;

export const TenantUserSummarySchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  email: z.string().email(),
  role: TenantUserRoleSchema,
  status: TenantUserStatusSchema,
  mustSetPassword: z.boolean(),
  lastLoginAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
});
export type TenantUserSummary = z.infer<typeof TenantUserSummarySchema>;

export const TenantAuthSessionSchema = z.object({
  user: TenantUserSummarySchema,
  tenant: z.object({
    id: z.string().uuid(),
    name: z.string(),
  }),
  accessToken: z.string(),
  accessTokenExpiresAt: z.string().datetime(),
  refreshToken: z.string(),
  refreshTokenExpiresAt: z.string().datetime(),
});
export type TenantAuthSession = z.infer<typeof TenantAuthSessionSchema>;

// Returned when the email matches more than one tenant — caller must
// pick one and re-POST with `tenantId`.
export const TenantAuthDisambiguationSchema = z.object({
  needsTenantSelection: z.literal(true),
  candidates: z.array(
    z.object({
      tenantId: z.string().uuid(),
      tenantName: z.string(),
    }),
  ),
});
export type TenantAuthDisambiguation = z.infer<
  typeof TenantAuthDisambiguationSchema
>;

export const TenantAuthLoginResponseSchema = z.union([
  TenantAuthSessionSchema,
  TenantAuthDisambiguationSchema,
]);

// ----- Auth: refresh / logout / set-password -----
export const TenantAuthRefreshRequestSchema = z.object({
  refreshToken: z.string().min(10),
});
export type TenantAuthRefreshRequest = z.infer<
  typeof TenantAuthRefreshRequestSchema
>;

export const TenantAuthLogoutRequestSchema = z.object({
  refreshToken: z.string().min(10),
});
export type TenantAuthLogoutRequest = z.infer<
  typeof TenantAuthLogoutRequestSchema
>;

export const TenantAuthSetPasswordRequestSchema = z.object({
  // currentPassword is required when the user is NOT in must_set_password
  // mode (normal change). For first-login set, it's omitted.
  currentPassword: z.string().min(1).max(200).optional(),
  newPassword: tenantUserPasswordSchema,
});
export type TenantAuthSetPasswordRequest = z.infer<
  typeof TenantAuthSetPasswordRequestSchema
>;

// ----- Users: list / create / update -----
export const TenantUsersListResponseSchema = z.object({
  users: z.array(TenantUserSummarySchema),
});
export type TenantUsersListResponse = z.infer<
  typeof TenantUsersListResponseSchema
>;

export const TenantUserCreateRequestSchema = z.object({
  email: z.string().email().max(254),
  // Initial password — the new user is created with must_set_password=true
  // so they're forced to change it on first login. Owner provides a
  // strong temporary value to share out-of-band.
  initialPassword: tenantUserPasswordSchema,
  role: z.enum(["MANAGER", "STAFF"]),
});
export type TenantUserCreateRequest = z.infer<
  typeof TenantUserCreateRequestSchema
>;

export const TenantUserUpdateRoleRequestSchema = z.object({
  role: z.enum(["MANAGER", "STAFF"]),
});
export type TenantUserUpdateRoleRequest = z.infer<
  typeof TenantUserUpdateRoleRequestSchema
>;

export const TenantUserUpdateStatusRequestSchema = z.object({
  status: TenantUserStatusSchema,
});
export type TenantUserUpdateStatusRequest = z.infer<
  typeof TenantUserUpdateStatusRequestSchema
>;

// ============================================================================
// Phase 45 — SMS credit packages + applications.
// ============================================================================
//
// Single source of truth for the prepaid SMS credit packages tenants
// can purchase. Prices are in PHP, fixed at the catalog level. Adding
// a new tier means appending here and re-deploying — applications
// snapshot credits + pesoAmount at create time, so existing PENDING
// rows are never silently re-priced. Removing a SKU is a breaking
// change for any in-flight application.
//
// Pricing rationale: a per-credit discount as packages get larger,
// rewarding tenants who pre-fund. 100 credits @ ₱1.00/credit baseline,
// scaling to ₱0.70/credit at the 5000-credit tier.

export const SMS_CREDIT_PACKAGES = [
  { sku: "PKG_100",  credits: 100,  pesoAmount: 100  },
  { sku: "PKG_500",  credits: 500,  pesoAmount: 450  },
  { sku: "PKG_1000", credits: 1000, pesoAmount: 800  },
  { sku: "PKG_5000", credits: 5000, pesoAmount: 3500 },
] as const;

export type SmsCreditPackageSku = (typeof SMS_CREDIT_PACKAGES)[number]["sku"];

export const SmsCreditPackageSkuSchema = z.enum([
  "PKG_100",
  "PKG_500",
  "PKG_1000",
  "PKG_5000",
]);

export function getSmsCreditPackage(sku: SmsCreditPackageSku) {
  const pkg = SMS_CREDIT_PACKAGES.find((p) => p.sku === sku);
  if (!pkg) {
    // Unreachable in TS; defensive at the runtime boundary.
    throw new Error(`Unknown SMS credit package SKU: ${sku}`);
  }
  return pkg;
}

// ── VAT helpers ────────────────────────────────────────────────────
// All catalog prices (SMS credit packages, subscription tiers) are
// stored / displayed VAT-EXCLUSIVE. Final amount the merchant pays
// (and that appears on the invoice) is `peso × (1 + VAT_RATE)`.
// Exposed here so client + server agree on the number.
export const VAT_RATE = 0.12;
export function addVat(amount: number): number {
  return Math.round(amount * (1 + VAT_RATE) * 100) / 100;
}
export function formatPesoWithVat(amount: number): string {
  // Formats e.g. 100 → "₱112.00". Uses 2 decimals because VAT
  // rounding (e.g. 450 × 1.12 = 504, but 800 × 1.12 = 896 — exact).
  // toFixed(2) keeps the column visually aligned regardless.
  const final = addVat(amount);
  return `₱${final.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

// Pending SMS credit applications auto-cancel after this many hours
// if payment hasn't been confirmed by an admin. Surfaced in the
// merchant UI as a deadline note + enforced server-side by the
// auto-cancel sweeper. Keep server + client in sync via this const.
export const SMS_CREDIT_APPLICATION_TTL_HOURS = 24;

// Status lifecycle: PENDING → APPROVED | REJECTED | CANCELLED.
// CANCELLED is set by the merchant withdrawing the request OR by the
// auto-cancel sweeper after SMS_CREDIT_APPLICATION_TTL_HOURS.
export const SmsCreditApplicationStatusSchema = z.enum([
  "PENDING",
  "APPROVED",
  "REJECTED",
  "CANCELLED",
]);
export type SmsCreditApplicationStatus = z.infer<
  typeof SmsCreditApplicationStatusSchema
>;

// Tenant create-application body. Catalog SKU only — credits +
// pesoAmount are looked up server-side from SMS_CREDIT_PACKAGES so
// the client cannot inflate the package or under-pay.
export const TenantSmsCreditApplicationCreateRequestSchema = z.object({
  packageSku: SmsCreditPackageSkuSchema,
});
export type TenantSmsCreditApplicationCreateRequest = z.infer<
  typeof TenantSmsCreditApplicationCreateRequestSchema
>;

// Wire shape for an application row, returned by both tenant- and
// admin-facing list endpoints. `tenantName` is only populated on the
// admin view (joined from the tenants table).
export const SmsCreditApplicationViewSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  tenantName: z.string().nullable(),
  packageSku: SmsCreditPackageSkuSchema,
  credits: z.number().int().positive(),
  pesoAmount: z.number().nonnegative(),
  status: SmsCreditApplicationStatusSchema,
  paymentReference: z.string().nullable(),
  adminNotes: z.string().nullable(),
  ledgerId: z.string().uuid().nullable(),
  decidedByAdminId: z.string().uuid().nullable(),
  decidedByAdminEmail: z.string().nullable(),
  decidedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type SmsCreditApplicationView = z.infer<
  typeof SmsCreditApplicationViewSchema
>;

// Admin approve body. Both fields optional — a "received cash, no
// reference" approval is valid. paymentReference is the audit handle
// (GCash ref, OR number, bank txn id); adminNotes is a free-form
// memo. Both are persisted onto the application row AND surfaced in
// the resulting TOP_UP ledger entry's reason for downstream
// reconciliation.
export const AdminSmsCreditApplicationApproveRequestSchema = z.object({
  paymentReference: z.string().trim().max(120).optional(),
  adminNotes: z.string().trim().max(500).optional(),
});
export type AdminSmsCreditApplicationApproveRequest = z.infer<
  typeof AdminSmsCreditApplicationApproveRequestSchema
>;

export const AdminSmsCreditApplicationRejectRequestSchema = z.object({
  // Required: explain why so the tenant gets a useful next-step
  // (e.g. "no payment received as of <date>; please re-submit").
  reason: z.string().trim().min(1).max(500),
});
export type AdminSmsCreditApplicationRejectRequest = z.infer<
  typeof AdminSmsCreditApplicationRejectRequestSchema
>;

// ----- Phase 47 — admin loyalty-mode change ------------------------
//
// Backs `GET /admin/tenants/:id/loyalty-mode/preview` and
// `POST /admin/tenants/:id/loyalty-mode`. The POST body REQUIRES an
// explicit `confirm:true` so a misrouted JSON body can't accidentally
// flip a tenant. The preview response carries the counts the modal
// renders ("X rewards will be archived, Y balances reset").
export const AdminChangeLoyaltyModeBodySchema = z.object({
  toMode: LoyaltyModeSchema,
  confirm: z.literal(true, {
    errorMap: () => ({ message: "confirm must be the literal `true`." }),
  }),
});
export type AdminChangeLoyaltyModeBody = z.infer<
  typeof AdminChangeLoyaltyModeBodySchema
>;

export const AdminLoyaltyModePreviewResponseSchema = z.object({
  tenantId: z.string().uuid(),
  currentMode: LoyaltyModeSchema,
  toMode: LoyaltyModeSchema,
  rewardsToArchive: z.number().int().nonnegative(),
  balancesToZero: z.number().int().nonnegative(),
  newPointsRule: PointsRuleSchema,
});
export type AdminLoyaltyModePreviewResponse = z.infer<
  typeof AdminLoyaltyModePreviewResponseSchema
>;

export const AdminChangeLoyaltyModeResponseSchema = z.object({
  tenantId: z.string().uuid(),
  fromMode: LoyaltyModeSchema,
  toMode: LoyaltyModeSchema,
  rewardsArchived: z.number().int().nonnegative(),
  balancesZeroed: z.number().int().nonnegative(),
  newPointsRule: PointsRuleSchema,
  auditId: z.string().uuid(),
  /**
   * The post-commit tenant snapshot. Mirrors the fields the admin
   * console keeps in its `Tenant` row state so the caller can
   * re-render without an extra fetch — and so the response is a
   * complete record of what changed (current mode, current rule)
   * rather than just a pair of from/to flags.
   */
  tenant: z.object({
    id: z.string().uuid(),
    loyaltyMode: LoyaltyModeSchema,
    pointsRule: PointsRuleSchema,
  }),
});
export type AdminChangeLoyaltyModeResponse = z.infer<
  typeof AdminChangeLoyaltyModeResponseSchema
>;
